import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container,
  Grid,
  Typography,
  Paper,
  Tabs,
  Tab,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  Chip,
  Alert,
  Tooltip,
  Switch,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  Snackbar,
  InputAdornment,
  Divider,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

// Logo import
import ClarisLogo from '../assets/claris_logo_transparent[67291746].png';

// Day-of-Week Sales Factors for display (matches inventoryAnalysisEngine.js)
// Day-of-Week Sales Factors by Platform - Real Data from 365 Days Sales Analysis
// Values represent "index to average" where 1.00 = average sales day
const DAY_OF_WEEK_FACTORS = {
  'ASH': { Mon: 1.16, Tue: 0.62, Wed: 0.69, Thu: 0.92, Fri: 1.08, Sat: 1.06, Sun: 1.63 },
  'ATL': { Mon: 1.01, Tue: 0.84, Wed: 0.91, Thu: 1.08, Fri: 1.11, Sat: 0.95, Sun: 1.09 },
  'ATL A&B': { Mon: 1.01, Tue: 0.84, Wed: 0.91, Thu: 1.08, Fri: 1.11, Sat: 0.95, Sun: 1.09 },
  'ATL E/T/ATR': { Mon: 1.01, Tue: 0.84, Wed: 0.91, Thu: 1.08, Fri: 1.11, Sat: 0.95, Sun: 1.09 },
  'BNA': { Mon: 1.58, Tue: 0.30, Wed: 0.39, Thu: 1.19, Fri: 1.15, Sat: 0.53, Sun: 1.86 },
  'CAE': { Mon: 0.98, Tue: 0.86, Wed: 0.85, Thu: 1.16, Fri: 1.33, Sat: 0.75, Sun: 1.07 },
  'GSO': { Mon: 1.15, Tue: 0.81, Wed: 0.87, Thu: 1.05, Fri: 1.19, Sat: 0.77, Sun: 1.14 },
  'MEM': { Mon: 1.01, Tue: 0.73, Wed: 0.87, Thu: 1.20, Fri: 1.15, Sat: 0.75, Sun: 1.29 },
  'PNS': { Mon: 1.07, Tue: 0.78, Wed: 0.79, Thu: 0.92, Fri: 1.00, Sat: 1.03, Sun: 1.41 },
  'RDU': { Mon: 1.04, Tue: 0.82, Wed: 0.91, Thu: 1.14, Fri: 1.15, Sat: 0.84, Sun: 1.10 },
  'SAV': { Mon: 1.08, Tue: 0.73, Wed: 0.78, Thu: 0.94, Fri: 1.05, Sat: 1.08, Sun: 1.34 },
  'TYS': { Mon: 1.18, Tue: 0.72, Wed: 0.78, Thu: 1.05, Fri: 1.14, Sat: 0.87, Sun: 1.25 }
};

// ── Tab Visibility grid constants ──────────────────────────────────────────
const ROLES = ['admin', 'manager', 'associate', 'report', 'display'];

const TAB_DEFS = [
  { key: 'obd-sign-on',    label: 'OBD Sign On' },
  { key: 'putaway',        label: 'Putaway' },
  { key: 'clock-in',       label: 'Activity Monitor' },
  { key: 'obd-dashboard',  label: 'DC Dashboard' },
  { key: 'reports',        label: 'Reports' },
  { key: 'labor-planner',  label: 'Labor Planner' },
  { key: 'configuration',  label: 'Config' },
  { key: 'kpi-dashboard',  label: 'KPI Dashboard' },
  { key: 'platform',       label: 'Platform' },
  { key: 'warehouse',      label: 'Warehouse' },
  { key: 'productivity',   label: 'Productivity' },
  { key: 'quality',        label: 'Quality' },
  { key: 'inventory',      label: 'Inventory' },
  { key: 'kpi',            label: 'KPI (legacy)' },
  { key: 'backroom-scans', label: 'Space' },
  { key: 'feedback',       label: 'Feedback' },
  { key: 'display',        label: 'Display Board' },
  { key: 'assignments',    label: 'Assignments' },
];

// Which tabs each role can access via the existing hard-coded permission system.
// A false here means the role can NEVER see this tab regardless of visibility config.
const PERMISSION_FLOOR = {
  admin:     { 'obd-sign-on':true,  'putaway':true,  'clock-in':true,  'obd-dashboard':true,  'reports':true,  'labor-planner':true,  'configuration':true,  'kpi-dashboard':true,  'platform':true,  'warehouse':true,  'productivity':true,  'quality':true,  'inventory':true,  'kpi':true,  'backroom-scans':true,  'feedback':true,  'display':true  , 'assignments':true },
  manager:   { 'obd-sign-on':true,  'putaway':true,  'clock-in':true,  'obd-dashboard':true,  'reports':true,  'labor-planner':true,  'configuration':true,  'kpi-dashboard':true,  'platform':false, 'warehouse':false, 'productivity':false, 'quality':false, 'inventory':false, 'kpi':false, 'backroom-scans':false, 'feedback':false, 'display':true , 'assignments':true },
  associate: { 'obd-sign-on':true,  'putaway':true,  'clock-in':true,  'obd-dashboard':false, 'reports':false, 'labor-planner':false, 'configuration':false, 'kpi-dashboard':false, 'platform':false, 'warehouse':false, 'productivity':false, 'quality':false, 'inventory':false, 'kpi':false, 'backroom-scans':false, 'feedback':false, 'display':false , 'assignments':false },
  report:    { 'obd-sign-on':false, 'putaway':true,  'clock-in':false, 'obd-dashboard':false, 'reports':false, 'labor-planner':false, 'configuration':false, 'kpi-dashboard':true,  'platform':false, 'warehouse':false, 'productivity':false, 'quality':false, 'inventory':false, 'kpi':true,  'backroom-scans':false, 'feedback':false, 'display':false , 'assignments':false },
  display:   { 'obd-sign-on':false, 'putaway':false, 'clock-in':false, 'obd-dashboard':false, 'reports':false, 'labor-planner':false, 'configuration':false, 'kpi-dashboard':false, 'platform':false, 'warehouse':false, 'productivity':false, 'quality':false, 'inventory':false, 'kpi':false, 'backroom-scans':false, 'feedback':false, 'display':true , 'assignments':false }
};
// ──────────────────────────────────────────────────────────────────────────


// ═══════════════════ Labor Standards tab ═══════════════════
// Effective-dated standards (seconds-based) + read-only recalculate preview.
// View: manager/admin. Edit + save: admin only. The expected-time FORMULA
// authority is backend/lib/laborStandards.js — the table's example column is
// server-computed; only the edit dialog's live example mirrors it locally.
const LS_API = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5001';
const lsHeaders = () => {
  const t = localStorage.getItem('authToken');
  return { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) };
};
const fmtSecs = (s) => {
  if (s === null || s === undefined || Number.isNaN(Number(s))) return '—';
  const m = Math.floor(s / 60), sec = Math.round(s % 60);
  return `${m}:${String(sec).padStart(2, '0')} min`;
};
// Local mirror of the shared formula for the LIVE example in the edit dialog
// only (authority: backend/lib/laborStandards.js — keep in sync).
const lsExpected = (perObd, perLine, perUnit, pfd, lines, units) => {
  const base = (Number(perObd) || 0) + (Number(perLine) || 0) * lines + (Number(perUnit) || 0) * units;
  const p = Number(pfd) || 0;
  if (p < 0 || p >= 100) return null;
  return base / (1 - p / 100);
};
// CONVENTION: performance is always displayed as "% to standard" (factor × 100,
// whole number) — never as a raw factor. Math stays factor-based underneath.
const pct = (v) => (v === null || v === undefined ? '—' : `${Math.round(v * 100)}%`);
const r1 = (v) => (v === null || v === undefined ? '—' : (Math.round(v * 10) / 10).toFixed(1));

// Tote estimate admin config (tote_estimate_enabled, default OFF). Math mirror
// of backend/lib/totes.js: dept = mapping[CATEGORY] else GenMerch;
// totes = max(1, ceil(units / units_per_tote)); cart line only when
// totes_per_cart is set AND exceeded. Independent of the standards-feedback
// and auto-assignment toggles.
function ToteEstimateSection({ isAdmin }) {
  const [cfg, setCfg] = useState(null);      // server state
  const [capDrafts, setCapDrafts] = useState({});  // dept -> string
  const [estDrafts, setEstDrafts] = useState({});  // dept -> bool ("estimate totes" flag)
  const [mapDrafts, setMapDrafts] = useState({});  // CATEGORY -> dept
  const [tpcDraft, setTpcDraft] = useState(null);  // string | null(untouched)
  const [newCat, setNewCat] = useState('');
  const [newCatDept, setNewCatDept] = useState('GenMerch');
  const [exCat, setExCat] = useState('CANDY');
  const [exUnits, setExUnits] = useState('250');
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${LS_API}/api/totes/config`, { headers: lsHeaders() });
      const d = await r.json().catch(() => null);
      if (r.ok && d) { setCfg(d); setCapDrafts({}); setEstDrafts({}); setMapDrafts({}); setTpcDraft(null); }
    } catch { /* section renders nothing without config */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!cfg) return null;
  const depts = Object.keys(cfg.capacities || {}).sort();
  const liveCap = (dept) => {
    const v = capDrafts[dept] !== undefined ? Number(capDrafts[dept]) : Number(cfg.capacities[dept]);
    return Number.isFinite(v) && v > 0 ? v : null;
  };
  const liveDept = (cat) => mapDrafts[cat] !== undefined ? mapDrafts[cat] : (cfg.mapping[cat] || 'GenMerch');
  // per-department "estimate totes" flag — false = no estimate/cart line for that dept
  const liveEst = (dept) => estDrafts[dept] !== undefined ? estDrafts[dept] : ((cfg.estimate_by_department || {})[dept] !== false);
  const mappedCats = Array.from(new Set([...Object.keys(cfg.mapping || {}), ...Object.keys(mapDrafts)])).sort();
  // live example from the CURRENT drafts
  const exDept = (cfg.mapping[exCat] !== undefined || mapDrafts[exCat] !== undefined) ? liveDept(exCat) : 'GenMerch';
  const exUpt = liveCap(exDept);
  const exTotes = liveEst(exDept) && exUpt && Number(exUnits) >= 0 ? Math.max(1, Math.ceil(Number(exUnits) / exUpt)) : null;
  const dirty = Object.keys(capDrafts).length || Object.keys(estDrafts).length || Object.keys(mapDrafts).length || (tpcDraft !== null);

  const saveAll = async () => {
    setSaving(true);
    try {
      const body = {};
      if (Object.keys(capDrafts).length) {
        body.capacities = {};
        for (const [d, v] of Object.entries(capDrafts)) body.capacities[d] = Number(v);
      }
      if (Object.keys(estDrafts).length) body.estimate_by_department = estDrafts;
      if (Object.keys(mapDrafts).length) body.mapping = mapDrafts;
      if (tpcDraft !== null) body.totes_per_cart = tpcDraft === '' ? null : Number(tpcDraft);
      const r = await fetch(`${LS_API}/api/totes/config`, {
        method: 'PUT', headers: lsHeaders(), body: JSON.stringify(body),
      });
      const d = await r.json().catch(() => null);
      if (r.ok && d) { setCfg(d); setCapDrafts({}); setEstDrafts({}); setMapDrafts({}); setTpcDraft(null); setSnack('Tote settings saved'); }
      else setSnack(d?.error || 'Failed to save tote settings');
    } catch { setSnack('Failed to save tote settings'); }
    setSaving(false);
  };
  const toggleEnabled = async (next) => {
    setSaving(true);
    try {
      const r = await fetch(`${LS_API}/api/totes/config`, {
        method: 'PUT', headers: lsHeaders(), body: JSON.stringify({ enabled: next }),
      });
      const d = await r.json().catch(() => null);
      if (r.ok && d) { setCfg(d); setSnack(`Tote estimates ${d.enabled ? 'enabled' : 'disabled'}.`); }
      else setSnack(d?.error || 'Could not save setting');
    } catch { setSnack('Could not save setting'); }
    setSaving(false);
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Tote Estimate</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Shows “About N totes” to associates at start, on the next-OBD popup, and on the kiosk.
        Estimate = units ÷ department units-per-tote, rounded up (minimum 1). Unmapped categories count as GenMerch.
      </Typography>
      <FormControlLabel
        control={<Switch checked={cfg.enabled} onChange={(e) => toggleEnabled(e.target.checked)} disabled={!isAdmin || saving} />}
        label="Show tote estimates to associates"
      />
      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mt: 1 }}>
        <Box>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>Units per tote · Estimate on/off</Typography>
          <Table size="small" sx={{ width: 360 }}>
            <TableBody>
              {depts.map((d) => (
                <TableRow key={d}>
                  <TableCell sx={{ py: 0.25, color: liveEst(d) ? undefined : 'text.disabled' }}>{d}</TableCell>
                  <TableCell align="right" sx={{ py: 0.25 }}>
                    <TextField
                      size="small" type="number" sx={{ width: 110 }}
                      inputProps={{ step: 0.1, min: 0.1 }}
                      disabled={!isAdmin || saving || !liveEst(d)}
                      value={capDrafts[d] !== undefined ? capDrafts[d] : String(cfg.capacities[d])}
                      onChange={(e) => setCapDrafts((c) => ({ ...c, [d]: e.target.value }))}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ py: 0.25, pl: 0 }}>
                    {/* per-department flag: OFF = no estimate or cart line
                        anywhere for this department's OBDs */}
                    <Switch
                      size="small"
                      checked={liveEst(d)}
                      disabled={!isAdmin || saving}
                      onChange={(e) => setEstDrafts((m) => ({ ...m, [d]: e.target.checked }))}
                      inputProps={{ 'aria-label': `Estimate totes for ${d}` }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TextField
            label="Totes per cart (empty = no cart line)"
            size="small" type="number" sx={{ mt: 1.5, width: 300 }}
            inputProps={{ step: 1, min: 1, max: 99 }}
            disabled={!isAdmin || saving}
            value={tpcDraft !== null ? tpcDraft : (cfg.totes_per_cart ?? '')}
            onChange={(e) => setTpcDraft(e.target.value)}
          />
        </Box>
        <Box>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>Category → department</Typography>
          <Table size="small" sx={{ width: 380 }}>
            <TableBody>
              {mappedCats.map((cat) => (
                <TableRow key={cat}>
                  <TableCell sx={{ py: 0.25 }}>{cat}</TableCell>
                  <TableCell align="right" sx={{ py: 0.25 }}>
                    <TextField
                      select size="small" sx={{ width: 150 }} disabled={!isAdmin || saving}
                      value={liveDept(cat)}
                      onChange={(e) => setMapDrafts((m) => ({ ...m, [cat]: e.target.value }))}
                    >
                      {depts.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                    </TextField>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {isAdmin && (
            <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center' }}>
              <TextField
                label="Add category" size="small" sx={{ width: 180 }}
                value={newCat} onChange={(e) => setNewCat(e.target.value.toUpperCase())}
              />
              <TextField select size="small" sx={{ width: 140 }} value={newCatDept} onChange={(e) => setNewCatDept(e.target.value)}>
                {depts.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
              </TextField>
              <Button
                size="small" disabled={!newCat.trim()}
                onClick={() => { setMapDrafts((m) => ({ ...m, [newCat.trim()]: newCatDept })); setNewCat(''); }}
              >
                Add
              </Button>
            </Box>
          )}
          {/* live example computed from the current (unsaved) values */}
          <Box sx={{ mt: 2, p: 1, backgroundColor: '#f5f5f5', borderRadius: 1, width: 380 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>Live example</Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
              <TextField select size="small" sx={{ width: 160 }} value={mappedCats.includes(exCat) ? exCat : (mappedCats[0] || '')} onChange={(e) => setExCat(e.target.value)}>
                {mappedCats.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
              <TextField size="small" type="number" sx={{ width: 90 }} value={exUnits} onChange={(e) => setExUnits(e.target.value)} label="Units" />
            </Box>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {exTotes
                ? `${exCat} OBD with ${Number(exUnits)} units = ${exTotes} tote${exTotes === 1 ? '' : 's'} (${exDept} · ${exUpt}/tote)`
                : !liveEst(exDept)
                ? `${exCat} shows no estimate — tote estimates are OFF for ${exDept}`
                : 'Enter units to see the estimate'}
            </Typography>
          </Box>
        </Box>
      </Box>
      {isAdmin && (
        <Button variant="contained" size="small" sx={{ mt: 1.5 }} disabled={!dirty || saving} onClick={saveAll}>
          Save tote settings
        </Button>
      )}
      <Snackbar open={!!snack} autoHideDuration={4000} onClose={() => setSnack(null)} message={snack || ''} />
    </Paper>
  );
}

// STORE PRIORITY (Oct 1): per-platform store ranking that orders the
// assignment queue (after departure + small-OBD flag, before size). Seed
// sales are reference-only and NEVER shown outside this admin screen.
// Admin edits (same gate as the auto-assign toggle); managers view.
function StorePriorityTab({ isAdmin }) {
  const [data, setData] = useState(null);
  const [plat, setPlat] = useState('');
  const [list, setList] = useState([]);       // current arrangement
  const [dirty, setDirty] = useState(false);
  const [drag, setDrag] = useState(null);     // { site, dropIndex }
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState(null);
  const listRef = useRef(null);
  const dragRef = useRef(null);

  const load = useCallback(async (keepPlat) => {
    try {
      const r = await fetch(`${LS_API}/api/store-priority`, { headers: lsHeaders() });
      const d = await r.json().catch(() => null);
      if (!r.ok || !d) return;
      setData(d);
      const code = keepPlat && d.platforms.some(p => p.platform_code === keepPlat)
        ? keepPlat : (d.platforms[0]?.platform_code || '');
      setPlat(code);
      setList(d.platforms.find(p => p.platform_code === code)?.stores || []);
      setDirty(false);
    } catch { /* tab renders empty */ }
  }, []);
  useEffect(() => { load(); }, [load]);
  const pickPlat = (code) => {
    setPlat(code);
    setList(data?.platforms.find(p => p.platform_code === code)?.stores || []);
    setDirty(false);
  };

  const dropIndexFor = (site, clientY) => {
    const cards = [...(listRef.current?.querySelectorAll('[data-site]') || [])]
      .filter(el => el.dataset.site !== site);
    let idx = 0;
    for (const el of cards) {
      const rect = el.getBoundingClientRect();
      if (clientY > rect.top + rect.height / 2) idx++;
    }
    return idx;
  };
  const onRowPointerDown = (e, store) => {
    if (!isAdmin || (e.button != null && e.button !== 0)) return;
    if (e.target.closest('button,input,.MuiSwitch-root')) return; // not from controls
    const d = { site: store.site_code, dropIndex: dropIndexFor(store.site_code, e.clientY) };
    dragRef.current = d; setDrag(d);
    const move = (ev) => {
      const cur = dragRef.current; if (!cur) return;
      const di = dropIndexFor(cur.site, ev.clientY);
      if (di !== cur.dropIndex) { dragRef.current = { ...cur, dropIndex: di }; setDrag(dragRef.current); }
      ev.preventDefault();
    };
    const up = () => {
      const cur = dragRef.current;
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      dragRef.current = null; setDrag(null);
      if (!cur) return;
      setList(prev => {
        const rest = prev.filter(s => s.site_code !== cur.site);
        const me = prev.find(s => s.site_code === cur.site);
        const next = [...rest.slice(0, cur.dropIndex), me, ...rest.slice(cur.dropIndex)];
        if (next.map(s => s.site_code).join() !== prev.map(s => s.site_code).join()) setDirty(true);
        return next;
      });
    };
    window.addEventListener('pointermove', move, { passive: false });
    window.addEventListener('pointerup', up);
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${LS_API}/api/store-priority/reorder`, {
        method: 'PUT', headers: lsHeaders(),
        body: JSON.stringify({ platform_code: plat, site_codes: list.map(s => s.site_code) }),
      });
      const d = await r.json().catch(() => null);
      if (r.ok) { setSnack('Store order saved — the queue follows it now'); load(plat); }
      else setSnack(d?.error || 'Failed to save');
    } catch { setSnack('Failed to save'); }
    setSaving(false);
  };
  const toggleActive = async (store, active) => {
    const r = await fetch(`${LS_API}/api/store-priority/site/${encodeURIComponent(store.site_code)}`, {
      method: 'PUT', headers: lsHeaders(), body: JSON.stringify({ active }),
    });
    if (r.ok) { setSnack(active ? 'Store activated' : 'Store deactivated (sorts after active stores)'); load(plat); }
  };
  const confirmPlacement = async (store) => {
    const r = await fetch(`${LS_API}/api/store-priority/site/${encodeURIComponent(store.site_code)}/confirm`, {
      method: 'POST', headers: lsHeaders(),
    });
    if (r.ok) { setSnack('Placement confirmed'); load(plat); }
  };

  if (!data) return <Box sx={{ p: 3 }}><Typography color="text.secondary">Loading store priority…</Typography></Box>;
  const platObj = data.platforms.find(p => p.platform_code === plat);
  let visIdx = 0;
  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 1 }}>
        <Typography variant="h6">Store Priority</Typography>
        <TextField select size="small" sx={{ minWidth: 240 }} value={plat} onChange={(e) => pickPlat(e.target.value)}>
          {data.platforms.map(p => (
            <MenuItem key={p.platform_code} value={p.platform_code}>
              {p.platform_name}{p.unmapped ? ' — Unmapped platform' : ''} ({p.stores.length})
            </MenuItem>
          ))}
        </TextField>
        {data.needs_placement > 0 && (
          <Chip color="warning" label={`${data.needs_placement} new store${data.needs_placement === 1 ? '' : 's'} need placement`} />
        )}
        <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
          <Button variant="contained" size="small" disabled={!isAdmin || !dirty || saving} onClick={save}>Save order</Button>
          <Button size="small" disabled={!dirty || saving} onClick={() => load(plat)}>Revert</Button>
        </Box>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        1 = highest priority within the platform — the assignment queue serves higher stores first
        (after departure and the small-OBD rule). Inactive stores always sort after active ones.
        Seed sales (8/1–10/1) are reference only and never shown to associates.
        {platObj?.unmapped ? ' This platform came from an OBD platform code with no seed mapping — rank its stores or map it.' : ''}
        {!isAdmin ? ' View only — edits are admin-only.' : ' Drag to reorder; moving a store confirms its placement.'}
      </Typography>
      <Box ref={listRef} sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, maxWidth: 760 }}>
        {(list || []).map((s) => {
          const dragged = drag && drag.site === s.site_code;
          const lineBefore = drag && !dragged && visIdx === drag.dropIndex;
          if (!dragged) visIdx++;
          return (
            <React.Fragment key={s.site_code}>
              {lineBefore && <Box sx={{ height: 3, bgcolor: '#3E63DD', borderRadius: 2 }} />}
              <Box
                data-site={s.site_code}
                onPointerDown={(e) => onRowPointerDown(e, s)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1.5, p: 1, pl: 1.5,
                  bgcolor: s.active ? '#fff' : '#F7F8FA', border: '1px solid #EAECF0', borderRadius: 2,
                  opacity: dragged ? 0.35 : 1, cursor: isAdmin ? 'grab' : 'default',
                  touchAction: 'none', userSelect: 'none',
                }}
              >
                <Typography variant="body2" sx={{ width: 28, fontWeight: 700, color: '#667085' }}>{list.indexOf(s) + 1}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, width: 56 }}>{s.site_code}</Typography>
                <Typography variant="body2" sx={{ flex: 1, color: s.active ? '#344054' : '#98A2B3' }} noWrap>
                  {s.site_name || '(name pending)'}
                </Typography>
                <Typography variant="caption" sx={{ color: '#98A2B3', width: 110, textAlign: 'right' }}>
                  {s.seed_sales != null ? `$${Math.round(Number(s.seed_sales)).toLocaleString()}` : '—'}
                </Typography>
                {s.needs_placement && (
                  <>
                    <Chip size="small" color="warning" label="Needs placement" sx={{ height: 20, fontSize: 11 }} />
                    {isAdmin && <Button size="small" sx={{ minWidth: 0, px: 0.75 }} onClick={() => confirmPlacement(s)}>Confirm</Button>}
                  </>
                )}
                {s.source === 'auto_added' && !s.needs_placement && (
                  <Chip size="small" label="auto-added" sx={{ height: 20, fontSize: 11, bgcolor: '#F2F4F7', color: '#667085' }} />
                )}
                <Switch size="small" checked={s.active} disabled={!isAdmin} onChange={(e) => toggleActive(s, e.target.checked)} />
              </Box>
            </React.Fragment>
          );
        })}
        {drag && drag.dropIndex >= visIdx && <Box sx={{ height: 3, bgcolor: '#3E63DD', borderRadius: 2 }} />}
        {!list.length && <Typography variant="caption" color="text.secondary">No stores for this platform.</Typography>}
      </Box>
      <Snackbar open={!!snack} autoHideDuration={4000} onClose={() => setSnack(null)} message={snack || ''} />
    </Box>
  );
}

function LaborStandardsTab({ isAdmin, currentUser }) {
  const [cats, setCats] = useState([]);
  const [standards, setStandards] = useState({});
  const [loading, setLoading] = useState(true);
  const [snack, setSnack] = useState(null);
  // edit dialog: values held as ENTERED units (per OBD = minutes, others = seconds)
  const [edit, setEdit] = useState(null);
  const [drafts, setDrafts] = useState({}); // category -> seconds-based std (preview-only)
  const [history, setHistory] = useState(null); // { category, rows }
  // preview
  const d30 = new Date(Date.now() - 30 * 864e5).toISOString().slice(0, 10);
  const tomorrow = new Date(Date.now() + 864e5).toISOString().slice(0, 10);
  const [pvStart, setPvStart] = useState(d30);
  const [pvEnd, setPvEnd] = useState(tomorrow);
  const [useDraft, setUseDraft] = useState(false);
  const [pv, setPv] = useState(null);
  const [pvLoading, setPvLoading] = useState(false);
  // "Show standards to associates" (standards_feedback_enabled, default OFF)
  const [feedbackEnabled, setFeedbackEnabled] = useState(false);
  const [feedbackSaving, setFeedbackSaving] = useState(false);
  const [feedbackThreshold, setFeedbackThresholdState] = useState(100);
  const [thresholdDraft, setThresholdDraft] = useState(null);
  // Shift labor settings for the outlook (both seed NULL — the outlook warns
  // and falls back to headcount until full_time_shift_hours is set)
  const [shiftCfg, setShiftCfg] = useState(null); // { full_time_shift_hours, unpaid_lunch_minutes }
  const [smallRuleGroups, setSmallRuleGroups] = useState([]); // per-group apply_small_rules
  const [shiftTriage, setShiftTriage] = useState(false); // truck_triage_enabled
  const [depPopup, setDepPopup] = useState({ enabled: true, snooze: 15 }); // departure_popup_*
  const [shiftDraft, setShiftDraft] = useState({}); // partial drafts
  const [shiftSaving, setShiftSaving] = useState(false);
  // Clock-based shift schedule (the anchor source — seeded values need
  // client confirmation before go-live)
  const [schedCfg, setSchedCfg] = useState(null);
  const [schedSaving, setSchedSaving] = useState(false);
  const [ovDraft, setOvDraft] = useState({ date: '', name: '', time: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${LS_API}/api/labor-standards`, { headers: lsHeaders() });
      const data = await res.json();
      if (res.ok) { setCats(data.categories || []); setStandards(data.standards || {}); }
      else setSnack(data.error || 'Failed to load labor standards');
      try {
        const gr = await fetch(`${LS_API}/api/assignments/groups`, { headers: lsHeaders() });
        const gd = await gr.json().catch(() => null);
        if (gr.ok && gd) setSmallRuleGroups(gd.groups || []);
        const cf = await fetch(`${LS_API}/api/assignments/config`, { headers: lsHeaders() });
        const cd = await cf.json().catch(() => null);
        if (cf.ok && cd) {
          setShiftTriage(cd.triage_enabled === true);
          setDepPopup({
            enabled: cd.departure_popup_enabled !== false,
            snooze: Number(cd.departure_popup_snooze_minutes) || 15,
          });
        }
      } catch { /* switches just stay empty */ }
      const fc = await fetch(`${LS_API}/api/labor-standards/feedback-config`, { headers: lsHeaders() });
      const fd = await fc.json().catch(() => null);
      if (fc.ok && fd) {
        setFeedbackEnabled(fd.enabled === true);
        if (Number.isFinite(Number(fd.threshold))) setFeedbackThresholdState(Number(fd.threshold));
      }
      const sc = await fetch(`${LS_API}/api/labor-standards/shift-config`, { headers: lsHeaders() });
      const sd = await sc.json().catch(() => null);
      if (sc.ok && sd) setShiftCfg(sd);
      const ss = await fetch(`${LS_API}/api/labor-standards/shift-schedule`, { headers: lsHeaders() });
      const ssd = await ss.json().catch(() => null);
      if (ss.ok && ssd) setSchedCfg(ssd);
    } catch { setSnack('Failed to load labor standards'); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openEdit = (cat) => {
    const s = drafts[cat] || standards[cat];
    setEdit({
      category: cat,
      perObdMin: s ? String(Number(s.per_obd_seconds) / 60) : '',
      perLineSec: s ? String(Number(s.per_line_seconds)) : '',
      perUnitSec: s ? String(Number(s.per_unit_seconds)) : '0',
      pfdPct: s ? String(Number(s.pfd_pct)) : '15',
      effectiveFrom: new Date().toISOString().slice(0, 10),
      error: '',
    });
  };
  const editToStd = () => ({
    per_obd_seconds: (Number(edit.perObdMin) || 0) * 60,
    per_line_seconds: Number(edit.perLineSec) || 0,
    per_unit_seconds: Number(edit.perUnitSec) || 0,
    pfd_pct: Number(edit.pfdPct),
  });
  const editValid = () => {
    if (!edit) return 'No form';
    const p = Number(edit.pfdPct);
    if (!Number.isFinite(p)) return 'PF&D must be a number';
    if (p < 0) return 'PF&D cannot be negative';
    if (p >= 100) return 'PF&D must be below 100';
    for (const [label, v] of [['Per OBD', edit.perObdMin], ['Per line', edit.perLineSec], ['Per unit', edit.perUnitSec]]) {
      if (v === '' || !Number.isFinite(Number(v)) || Number(v) < 0) return `${label} must be a non-negative number`;
    }
    if (!edit.effectiveFrom) return 'Effective date is required';
    return null;
  };
  const saveEdit = async () => {
    const err = editValid();
    if (err) { setEdit({ ...edit, error: err }); return; }
    try {
      const res = await fetch(`${LS_API}/api/labor-standards/${encodeURIComponent(edit.category)}`, {
        method: 'PUT', headers: lsHeaders(),
        body: JSON.stringify({ ...editToStd(), effective_from: edit.effectiveFrom }),
      });
      const data = await res.json();
      if (!res.ok) { setEdit({ ...edit, error: data.error || 'Save failed' }); return; }
      setDrafts((d) => { const n = { ...d }; delete n[edit.category]; return n; });
      setEdit(null);
      setSnack(`Saved ${data.category} — effective ${String(data.effective_from).slice(0, 10)}`);
      load();
    } catch { setEdit({ ...edit, error: 'Save failed' }); }
  };
  const applyDraft = () => {
    const err = editValid();
    if (err) { setEdit({ ...edit, error: err }); return; }
    setDrafts((d) => ({ ...d, [edit.category]: editToStd() }));
    setUseDraft(true);
    setEdit(null);
    setSnack(`Draft set for ${edit.category} — preview only, nothing saved`);
  };
  const openHistory = async (cat) => {
    try {
      const res = await fetch(`${LS_API}/api/labor-standards/history/${encodeURIComponent(cat)}`, { headers: lsHeaders() });
      const rows = await res.json();
      if (res.ok) setHistory({ category: cat, rows });
    } catch { setSnack('Failed to load history'); }
  };
  const runPreview = async () => {
    setPvLoading(true); setPv(null);
    try {
      const res = await fetch(`${LS_API}/api/labor-standards/preview`, {
        method: 'POST', headers: lsHeaders(),
        body: JSON.stringify({
          startDate: `${pvStart}T00:00:00Z`, endDate: `${pvEnd}T23:59:59Z`,
          ...(useDraft && Object.keys(drafts).length ? { draft: drafts } : {}),
        }),
      });
      const data = await res.json();
      if (res.ok) setPv(data); else setSnack(data.error || 'Preview failed');
    } catch { setSnack('Preview failed'); }
    setPvLoading(false);
  };
  const exportPreviewCSV = () => {
    if (!pv) return;
    const esc = (v) => { const s = String(v ?? ''); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const head = ['obd_number', 'category', 'lines', 'units', 'associate', 'elapsed_min', 'active_min',
      'base_expected_min', 'expected_with_pfd_min', 'pct_to_standard_elapsed', 'pct_to_standard_active', 'standard'];
    const lines = [head.join(',')];
    for (const r of pv.detail) {
      lines.push([r.obd_number, r.category, r.lines, r.units, r.associate, r.elapsed_min, r.active_min,
        r.no_standard ? 'No standard' : r.base_expected_min,
        r.no_standard ? 'No standard' : r.expected_with_pfd_min,
        r.no_standard ? '' : Math.round(r.pf_elapsed * 100), r.no_standard || r.pf_active == null ? '' : Math.round(r.pf_active * 100),
        r.no_standard ? 'No standard' : (pv.draft_applied && drafts[r.category] ? 'draft' : 'saved')].map(esc).join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `labor-standards-preview-${pvStart}_to_${pvEnd}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  const toggleFeedback = async (next) => {
    setFeedbackSaving(true);
    try {
      const res = await fetch(`${LS_API}/api/labor-standards/feedback-config`, {
        method: 'PUT', headers: lsHeaders(), body: JSON.stringify({ enabled: next }),
      });
      const data = await res.json();
      if (!res.ok) { setSnack(data.error || 'Could not save setting'); return; }
      setFeedbackEnabled(data.enabled === true);
      setSnack(`Standards feedback for associates ${data.enabled ? 'enabled' : 'disabled'}.`);
    } catch { setSnack('Could not save setting'); }
    setFeedbackSaving(false);
  };

  const basisRows = (a) => [['ELAPSED', a.elapsed], ['ACTIVE', a.active]];
  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>Labor Standards</Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        Effective-dated standards: saving creates a new version and closes the old one — history is never rewritten.
        Expected time = (per-OBD + per-line × lines + per-unit × units) ÷ (1 − PF&D%).
        {!isAdmin && ' You have view access; only admins can edit.'}
      </Alert>
      <FormControlLabel
        sx={{ mb: 1 }}
        control={
          <Switch
            checked={feedbackEnabled}
            onChange={(e) => toggleFeedback(e.target.checked)}
            disabled={!isAdmin || feedbackSaving}
          />
        }
        label="Show standards to associates (expected time at start, % to standard at complete)"
      />
      {/* completion-popup tier cut: >= threshold = "Good job", below = "Needs improvement" */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <TextField
          label="Good-job threshold (% to standard)"
          type="number"
          size="small"
          inputProps={{ step: 1, min: 50, max: 200 }}
          sx={{ width: 240 }}
          disabled={!isAdmin || feedbackSaving}
          value={thresholdDraft ?? feedbackThreshold}
          onChange={(e) => setThresholdDraft(e.target.value)}
        />
        <Button
          size="small" variant="outlined"
          disabled={!isAdmin || feedbackSaving || thresholdDraft == null || thresholdDraft === ''}
          onClick={async () => {
            setFeedbackSaving(true);
            try {
              const r = await fetch(`${LS_API}/api/labor-standards/feedback-config`, {
                method: 'PUT', headers: lsHeaders(), body: JSON.stringify({ threshold: Number(thresholdDraft) }),
              });
              const d = await r.json().catch(() => null);
              if (r.ok && d) { setFeedbackThresholdState(Number(d.threshold)); setThresholdDraft(null); setSnack(`Threshold set to ${d.threshold}%`); }
              else setSnack(d?.error || 'Failed to save threshold');
            } catch { setSnack('Failed to save threshold'); }
            setFeedbackSaving(false);
          }}
        >
          Save
        </Button>
        <Typography variant="caption" color="text.secondary">
          Completion popup: at or above = “Good job”, below = “Needs improvement”.
        </Typography>
      </Box>
      {/* Clock-based SHIFT SCHEDULE — the anchor source (lib/shiftSchedule.js).
          Imports no longer define shifts; exceptions go in date overrides.
          EFFECTIVE-DATED since Nov 2026: `revisions` carry time changes with an
          effective date; the editor edits the ACTIVE revision and shows the
          older definitions read-only so history keeps resolving correctly. */}
      {schedCfg && (() => {
        const todayET = new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
        const revs = schedCfg.revisions || [];
        const activeRevIdx = revs.reduce((best, r, i) =>
          (r && r.effective_from <= todayET && (best < 0 || r.effective_from > revs[best].effective_from)) ? i : best, -1);
        const activeShifts = activeRevIdx >= 0 ? revs[activeRevIdx].shifts : (schedCfg.shifts || []);
        const patchActiveShift = (i, patch) => setSchedCfg(c => {
          if (activeRevIdx >= 0) {
            const nr = (c.revisions || []).map((r, k) => k === activeRevIdx
              ? { ...r, shifts: r.shifts.map((x, j) => j === i ? { ...x, ...patch } : x) } : r);
            return { ...c, revisions: nr };
          }
          return { ...c, shifts: c.shifts.map((x, j) => j === i ? { ...x, ...patch } : x) };
        });
        const fmt12 = (hhmm) => {
          const [h, m] = String(hhmm || '').split(':').map(Number);
          if (!Number.isFinite(h)) return hhmm || '—';
          const ap = h >= 12 ? 'PM' : 'AM'; const h12 = ((h + 11) % 12) + 1;
          return `${h12}:${String(m).padStart(2, '0')} ${ap}`;
        };
        return (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Shift Schedule</Typography>
          <Alert severity="info" sx={{ my: 1 }}>
            <strong>Effective Mon Nov 2, 2026 (client-confirmed):</strong> Shift 1 (night) = 3:00 PM – 11:00 PM ET,
            Shift 2 (morning) = 4:00 AM – 12:00 PM ET. Shift changes (assignments, boosts, pins, pace, day stats)
            flip at these times; imports never set them. Times are effective-dated — dates before Nov 2 still
            resolve with the old schedule, so historical reports are unchanged. Edits below change the
            schedule in force today{activeRevIdx >= 0 ? ` (revision effective ${revs[activeRevIdx].effective_from})` : ''}.
          </Alert>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            {activeShifts.map((s, i) => (
              <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  label={`Shift ${i + 1} name`} size="small" sx={{ width: 130 }} disabled={!isAdmin || schedSaving}
                  value={s.name}
                  onChange={(e) => patchActiveShift(i, { name: e.target.value })}
                />
                <TextField
                  label="Start (ET, 24h)" size="small" sx={{ width: 120 }} disabled={!isAdmin || schedSaving}
                  value={s.start_time} placeholder="15:00"
                  onChange={(e) => patchActiveShift(i, { start_time: e.target.value })}
                />
                <TextField
                  label="End (ET, 24h)" size="small" sx={{ width: 120 }} disabled={!isAdmin || schedSaving}
                  value={s.end_time || ''} placeholder="23:00"
                  onChange={(e) => patchActiveShift(i, { end_time: e.target.value || undefined })}
                />
              </Box>
            ))}
            <TextField
              label="Setup window (hours)" size="small" type="number" sx={{ width: 160 }}
              inputProps={{ step: 0.5, min: 0.5, max: 12 }} disabled={!isAdmin || schedSaving}
              value={schedCfg.setup_window_hours}
              onChange={(e) => setSchedCfg(c => ({ ...c, setup_window_hours: e.target.value }))}
            />
          </Box>
          {/* per-date overrides for exceptions */}
          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mt: 1.5 }}>Date overrides (exceptions)</Typography>
          {Object.entries(schedCfg.date_overrides || {}).map(([d, m]) =>
            Object.entries(m).map(([nm, tm]) => (
              <Box key={`${d}-${nm}`} sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
                <Typography variant="body2" sx={{ width: 240 }}>{d} · {nm} → {tm} ET</Typography>
                {isAdmin && (
                  <Button size="small" color="error" onClick={() => setSchedCfg(c => {
                    const ov = { ...(c.date_overrides || {}) };
                    const row = { ...ov[d] }; delete row[nm];
                    if (Object.keys(row).length) ov[d] = row; else delete ov[d];
                    return { ...c, date_overrides: ov };
                  })}>Remove</Button>
                )}
              </Box>
            )))}
          {isAdmin && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
              <TextField label="Date (YYYY-MM-DD)" size="small" sx={{ width: 160 }} value={ovDraft.date} onChange={(e) => setOvDraft(o => ({ ...o, date: e.target.value }))} />
              <TextField select size="small" sx={{ width: 130 }} label="Shift" value={ovDraft.name} onChange={(e) => setOvDraft(o => ({ ...o, name: e.target.value }))}>
                {activeShifts.map(s => <MenuItem key={s.name} value={s.name}>{s.name}</MenuItem>)}
              </TextField>
              <TextField label="Start (HH:MM)" size="small" sx={{ width: 120 }} value={ovDraft.time} onChange={(e) => setOvDraft(o => ({ ...o, time: e.target.value }))} />
              <Button size="small" disabled={!ovDraft.date || !ovDraft.name || !ovDraft.time} onClick={() => {
                setSchedCfg(c => ({
                  ...c,
                  date_overrides: { ...(c.date_overrides || {}), [ovDraft.date]: { ...((c.date_overrides || {})[ovDraft.date] || {}), [ovDraft.name]: ovDraft.time } },
                }));
                setOvDraft({ date: '', name: '', time: '' });
              }}>Add override</Button>
            </Box>
          )}
          {isAdmin && (
            <Button variant="contained" size="small" sx={{ mt: 1.5 }} disabled={schedSaving} onClick={async () => {
              setSchedSaving(true);
              try {
                const r = await fetch(`${LS_API}/api/labor-standards/shift-schedule`, {
                  method: 'PUT', headers: lsHeaders(),
                  body: JSON.stringify({ ...schedCfg, setup_window_hours: Number(schedCfg.setup_window_hours) }),
                });
                const d = await r.json().catch(() => null);
                if (r.ok && d) { setSchedCfg(d); setSnack('Shift schedule saved'); }
                else setSnack(d?.error || 'Failed to save shift schedule');
              } catch { setSnack('Failed to save shift schedule'); }
              setSchedSaving(false);
            }}>Save schedule</Button>
          )}
          {/* read-only schedule history: every definition set with when it applies */}
          {activeRevIdx >= 0 && (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>Schedule history (read-only)</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Before {revs[0].effective_from}: {(schedCfg.shifts || []).map(s =>
                  `${s.name} ${fmt12(s.start_time)}–${s.end_time ? fmt12(s.end_time) : '—'}`).join(' · ')}
              </Typography>
              {revs.map((r, i) => (
                <Typography key={i} variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  From {r.effective_from}: {(r.shifts || []).map(s =>
                    `${s.name} ${fmt12(s.start_time)}–${s.end_time ? fmt12(s.end_time) : '—'}`).join(' · ')}
                  {i === activeRevIdx ? ' (in force today — edited above)' : ''}
                </Typography>
              ))}
            </Box>
          )}
        </Paper>
        );
      })()}

      {/* Shift labor settings — the outlook's labor-hours model. NULL shift
          hours = the outlook warns and falls back to clocked-in headcount. */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Shift Labor (outlook)</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Each picker contributes their remaining hours: full-time shift hours (or their part-time expected
          hours from the Assignments roster) minus time since clock-in, minus unpaid lunch until taken.
          Breaks are not deducted — PF&D covers them.
        </Typography>
        {shiftCfg && shiftCfg.full_time_shift_hours == null && (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            Set full-time shift hours — until then the shift outlook falls back to clocked-in headcount.
          </Alert>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <TextField
            label="Full-time shift hours"
            type="number" size="small" sx={{ width: 190 }}
            inputProps={{ step: 0.5, min: 1, max: 24 }}
            disabled={!isAdmin || shiftSaving}
            value={shiftDraft.full_time_shift_hours ?? (shiftCfg?.full_time_shift_hours ?? '')}
            onChange={(e) => setShiftDraft((d) => ({ ...d, full_time_shift_hours: e.target.value }))}
          />
          <TextField
            label="Unpaid lunch (minutes)"
            type="number" size="small" sx={{ width: 190 }}
            inputProps={{ step: 5, min: 0, max: 120 }}
            disabled={!isAdmin || shiftSaving}
            value={shiftDraft.unpaid_lunch_minutes ?? (shiftCfg?.unpaid_lunch_minutes ?? '')}
            onChange={(e) => setShiftDraft((d) => ({ ...d, unpaid_lunch_minutes: e.target.value }))}
          />
          <TextField
            label="Idle warning (minutes)"
            type="number" size="small" sx={{ width: 170 }}
            inputProps={{ step: 1, min: 1, max: 240 }}
            disabled={!isAdmin || shiftSaving}
            value={shiftDraft.idle_warning_minutes ?? (shiftCfg?.idle_warning_minutes ?? 10)}
            onChange={(e) => setShiftDraft((d) => ({ ...d, idle_warning_minutes: e.target.value }))}
          />
          <TextField
            label="Lunch only if shift > (hours)"
            type="number" size="small" sx={{ width: 190 }}
            inputProps={{ step: 0.5, min: 0, max: 24 }}
            disabled={!isAdmin || shiftSaving}
            value={shiftDraft.lunch_min_shift_hours ?? (shiftCfg?.lunch_min_shift_hours ?? 6)}
            onChange={(e) => setShiftDraft((d) => ({ ...d, lunch_min_shift_hours: e.target.value }))}
          />
          {/* Rebalancer thresholds (home + smart loans) — admin-tunable */}
          {(shiftCfg?.rebalance_thresholds) && [
            ['short_h', 'Short below (h)'], ['return_h', 'Return above (h)'],
            ['cooldown_min', 'Cooldown (min)'], ['part_timer_floor_h', 'Donor floor (h)'],
          ].map(([k, label]) => (
            <TextField
              key={k} label={label} type="number" size="small" sx={{ width: 140 }}
              inputProps={{ step: k === 'cooldown_min' ? 5 : 0.25, min: 0 }}
              disabled={!isAdmin || shiftSaving}
              value={shiftDraft[k] ?? shiftCfg.rebalance_thresholds[k]}
              onChange={(e) => setShiftDraft((d) => ({ ...d, [k]: e.target.value }))}
            />
          ))}
          {/* PRIORITY CATEGORIES (ordered, covered first when a checkpoint
              can't be fully covered) + SMALL OBDS LAST threshold */}
          <TextField
            label="Priority categories (ordered, comma-separated)"
            size="small" sx={{ width: 320 }}
            disabled={!isAdmin || shiftSaving}
            value={shiftDraft.priority_categories ?? ((shiftCfg?.priority_categories || []).join(', '))}
            onChange={(e) => setShiftDraft((d) => ({ ...d, priority_categories: e.target.value }))}
            helperText="Covered first, in this order, when a truck can't be fully covered"
          />
          <TextField
            select
            label="Small OBD metric"
            size="small" sx={{ width: 150 }}
            disabled={!isAdmin || shiftSaving}
            value={shiftDraft.small_metric ?? (shiftCfg?.small_obd_threshold?.metric ?? 'lines')}
            onChange={(e) => setShiftDraft((d) => ({ ...d, small_metric: e.target.value }))}
            helperText="Client thinks in items (units)"
          >
            <MenuItem value="lines">Lines</MenuItem>
            <MenuItem value="units">Units (items)</MenuItem>
          </TextField>
          <TextField
            label="Small OBD under"
            type="number" size="small" sx={{ width: 150 }}
            inputProps={{ step: 1, min: 0, max: 10000 }}
            disabled={!isAdmin || shiftSaving}
            value={shiftDraft.small_value ?? (shiftCfg?.small_obd_threshold?.value ?? 10)}
            onChange={(e) => setShiftDraft((d) => ({ ...d, small_value: e.target.value }))}
            helperText="Sorts after larger OBDs, same departure"
          />
          <TextField
            label="Missed grace (min)"
            type="number" size="small" sx={{ width: 160 }}
            inputProps={{ step: 5, min: 5, max: 240 }}
            disabled={!isAdmin || shiftSaving}
            value={shiftDraft.missed_grace_minutes ?? (shiftCfg?.missed_grace_minutes ?? 45)}
            onChange={(e) => setShiftDraft((d) => ({ ...d, missed_grace_minutes: e.target.value }))}
            helperText="Unmarked truck auto-departs after this"
          />
          <TextField
            label="Fit pace factor"
            type="number" size="small" sx={{ width: 170 }}
            inputProps={{ step: 0.05, min: 0.1, max: 1 }}
            disabled={!isAdmin || shiftSaving}
            value={shiftDraft.fit_pace_factor ?? (shiftCfg?.fit_pace_factor ?? 0.8)}
            onChange={(e) => setShiftDraft((d) => ({ ...d, fit_pace_factor: e.target.value }))}
            helperText="End-of-shift fit: expected ÷ this"
          />
          <TextField
            label="Fit buffer (min)"
            type="number" size="small" sx={{ width: 160 }}
            inputProps={{ step: 1, min: 0, max: 120 }}
            disabled={!isAdmin || shiftSaving}
            value={shiftDraft.fit_buffer_minutes ?? (shiftCfg?.fit_buffer_minutes ?? 5)}
            onChange={(e) => setShiftDraft((d) => ({ ...d, fit_buffer_minutes: e.target.value }))}
            helperText="Added on top before the end time"
          />
          {/* TRUCK TRIAGE (Oct 9, default OFF): protect the next truck when
              chasing a projected-miss truck would sink it too. */}
          <FormControlLabel
            sx={{ ml: 0.5 }}
            control={(
              <Switch
                size="small"
                checked={!!shiftTriage}
                disabled={!isAdmin}
                onChange={async (e) => {
                  const enabled = e.target.checked;
                  try {
                    const r = await fetch(`${LS_API}/api/assignments/config`, {
                      method: 'PUT', headers: lsHeaders(), body: JSON.stringify({ triage_enabled: enabled }),
                    });
                    const d = await r.json().catch(() => null);
                    if (!r.ok) { setSnack(d?.error || 'Failed to save truck triage'); return; }
                    setShiftTriage(d?.triage_enabled === true);
                    setSnack(`Truck triage ${enabled ? 'ON' : 'OFF'}`);
                  } catch { setSnack('Failed to save truck triage'); }
                }}
              />
            )}
            label={<Typography variant="body2">Truck triage (shift to the next truck when chasing would sink it)</Typography>}
          />
          {/* DEPARTURE POPUP (Oct 2026, default ON): admins/managers get a
              popup when a truck's scheduled time passes unmarked. Snooze and
              dismiss are per user; the default snooze is set here. */}
          <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <FormControlLabel
              sx={{ ml: 0.5 }}
              control={(
                <Switch
                  size="small"
                  checked={!!depPopup.enabled}
                  disabled={!isAdmin}
                  onChange={async (e) => {
                    const enabled = e.target.checked;
                    try {
                      const r = await fetch(`${LS_API}/api/assignments/config`, {
                        method: 'PUT', headers: lsHeaders(), body: JSON.stringify({ departure_popup_enabled: enabled }),
                      });
                      const d = await r.json().catch(() => null);
                      if (!r.ok) { setSnack(d?.error || 'Failed to save departure popup'); return; }
                      setDepPopup((p) => ({ ...p, enabled: d?.departure_popup_enabled !== false }));
                      setSnack(`Departure popup ${enabled ? 'ON' : 'OFF'}`);
                    } catch { setSnack('Failed to save departure popup'); }
                  }}
                />
              )}
              label={<Typography variant="body2">Departure popup (remind leads when a truck is due and unmarked)</Typography>}
            />
            <TextField
              size="small" label="Default snooze (min)" type="number" sx={{ width: 160 }}
              value={depPopup.snooze} disabled={!isAdmin}
              inputProps={{ min: 5, max: 60, step: 5 }}
              onChange={(e) => setDepPopup((p) => ({ ...p, snooze: e.target.value }))}
              onBlur={async () => {
                const n = Number(depPopup.snooze);
                if (!Number.isInteger(n) || n < 5 || n > 60 || n % 5 !== 0) {
                  setSnack('Snooze must be 5-60 minutes in 5-minute steps'); return;
                }
                try {
                  const r = await fetch(`${LS_API}/api/assignments/config`, {
                    method: 'PUT', headers: lsHeaders(), body: JSON.stringify({ departure_popup_snooze_minutes: n }),
                  });
                  const d = await r.json().catch(() => null);
                  if (!r.ok) { setSnack(d?.error || 'Failed to save snooze default'); return; }
                  setDepPopup((p) => ({ ...p, snooze: Number(d?.departure_popup_snooze_minutes) || n }));
                  setSnack(`Default snooze ${n} min`);
                } catch { setSnack('Failed to save snooze default'); }
              }}
            />
          </Box>
          {/* PER-GROUP SMALL-OBD RULES (Oct 9): OFF = that group's queue
              ignores small-last, and the future small-overflow feature
              skips its pickers. Saved immediately per switch (audited). */}
          <Box sx={{ width: '100%', mt: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Apply small-OBD rules per group
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                off = that group ignores small-last ordering
              </Typography>
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {(smallRuleGroups || []).map((g) => (
                <FormControlLabel
                  key={g.id}
                  sx={{ mr: 1.5 }}
                  control={(
                    <Switch
                      size="small"
                      checked={g.apply_small_rules}
                      disabled={!isAdmin}
                      onChange={async (e) => {
                        const enabled = e.target.checked;
                        try {
                          const r = await fetch(`${LS_API}/api/assignments/groups/${g.id}/small-rules`, {
                            method: 'PUT', headers: lsHeaders(), body: JSON.stringify({ enabled }),
                          });
                          if (!r.ok) { setSnack('Failed to save small-OBD rule'); return; }
                          setSmallRuleGroups((gs) => gs.map((x) => (x.id === g.id ? { ...x, apply_small_rules: enabled } : x)));
                          setSnack(`Small-OBD rules ${enabled ? 'on' : 'off'} for ${g.name}`);
                        } catch { setSnack('Failed to save small-OBD rule'); }
                      }}
                    />
                  )}
                  label={<Typography variant="caption">{g.name}</Typography>}
                />
              ))}
            </Box>
          </Box>
          <Button
            size="small" variant="outlined"
            disabled={!isAdmin || shiftSaving || Object.keys(shiftDraft).length === 0}
            onClick={async () => {
              setShiftSaving(true);
              try {
                const body = {};
                const thKeys = ['short_h', 'return_h', 'cooldown_min', 'part_timer_floor_h'];
                if (thKeys.some((k) => shiftDraft[k] !== undefined)) {
                  body.rebalance_thresholds = { ...shiftCfg.rebalance_thresholds };
                  for (const k of thKeys) if (shiftDraft[k] !== undefined) body.rebalance_thresholds[k] = Number(shiftDraft[k]);
                }
                if (shiftDraft.full_time_shift_hours !== undefined) {
                  body.full_time_shift_hours = shiftDraft.full_time_shift_hours === '' ? null : Number(shiftDraft.full_time_shift_hours);
                }
                if (shiftDraft.unpaid_lunch_minutes !== undefined) {
                  body.unpaid_lunch_minutes = shiftDraft.unpaid_lunch_minutes === '' ? null : Number(shiftDraft.unpaid_lunch_minutes);
                }
                if (shiftDraft.lunch_min_shift_hours !== undefined) {
                  body.lunch_min_shift_hours = Number(shiftDraft.lunch_min_shift_hours);
                }
                if (shiftDraft.idle_warning_minutes !== undefined) {
                  body.idle_warning_minutes = Number(shiftDraft.idle_warning_minutes);
                }
                if (shiftDraft.priority_categories !== undefined) {
                  body.priority_categories = shiftDraft.priority_categories
                    .split(',').map((x) => x.trim()).filter(Boolean);
                }
                if (shiftDraft.small_metric !== undefined || shiftDraft.small_value !== undefined) {
                  body.small_obd_threshold = {
                    metric: shiftDraft.small_metric ?? (shiftCfg?.small_obd_threshold?.metric ?? 'lines'),
                    value: Number(shiftDraft.small_value ?? (shiftCfg?.small_obd_threshold?.value ?? 10)),
                  };
                }
                if (shiftDraft.missed_grace_minutes !== undefined) {
                  body.missed_grace_minutes = Number(shiftDraft.missed_grace_minutes);
                }
                if (shiftDraft.fit_pace_factor !== undefined) {
                  body.fit_pace_factor = Number(shiftDraft.fit_pace_factor);
                }
                if (shiftDraft.fit_buffer_minutes !== undefined) {
                  body.fit_buffer_minutes = Number(shiftDraft.fit_buffer_minutes);
                }
                const r = await fetch(`${LS_API}/api/labor-standards/shift-config`, {
                  method: 'PUT', headers: lsHeaders(), body: JSON.stringify(body),
                });
                const d = await r.json().catch(() => null);
                if (r.ok && d) { setShiftCfg(d); setShiftDraft({}); setSnack('Shift labor settings saved'); }
                else setSnack(d?.error || 'Failed to save shift labor settings');
              } catch { setSnack('Failed to save shift labor settings'); }
              setShiftSaving(false);
            }}
          >
            Save
          </Button>
        </Box>
      </Paper>
      <ToteEstimateSection isAdmin={isAdmin} />
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Per OBD</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Per line (s)</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Per unit (s)</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>PF&D %</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>10-line example</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Effective</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Last changed by</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {cats.map((cat) => {
              const s = standards[cat];
              const dr = drafts[cat];
              return (
                <TableRow key={cat} hover>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {cat}
                    {dr && <Chip label="draft" size="small" color="warning" sx={{ ml: 1, height: 18 }} />}
                  </TableCell>
                  {s ? (
                    <>
                      <TableCell align="right">{fmtSecs(s.per_obd_seconds)}</TableCell>
                      <TableCell align="right">{Number(s.per_line_seconds)}</TableCell>
                      <TableCell align="right">{Number(s.per_unit_seconds)}</TableCell>
                      <TableCell align="right">{Number(s.pfd_pct)}%</TableCell>
                      <TableCell align="right">{fmtSecs(s.example_10_line_seconds)}</TableCell>
                      <TableCell>{String(s.effective_from).slice(0, 10)}</TableCell>
                      <TableCell>{s.updated_by || '—'}</TableCell>
                    </>
                  ) : (
                    <TableCell colSpan={7} sx={{ color: 'text.secondary', fontStyle: 'italic' }}>No standard</TableCell>
                  )}
                  <TableCell>
                    <Button size="small" onClick={() => openEdit(cat)} disabled={!isAdmin}>
                      {s ? 'Edit' : 'Set standard'}
                    </Button>
                    <Button size="small" onClick={() => openHistory(cat)} disabled={!s}>History</Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ── Recalculate preview ── */}
      <Typography variant="h6" sx={{ mt: 4 }} gutterBottom>Recalculate Preview</Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        Read-only backtest against completed OBDs. Saved mode evaluates each OBD against the version effective at its
        start time; draft mode applies your unsaved draft values to the whole range as a what-if.
      </Alert>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
        <TextField label="From" type="date" size="small" value={pvStart} onChange={(e) => setPvStart(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField label="To" type="date" size="small" value={pvEnd} onChange={(e) => setPvEnd(e.target.value)} InputLabelProps={{ shrink: true }} />
        <FormControlLabel
          control={<Switch checked={useDraft} onChange={(e) => setUseDraft(e.target.checked)} disabled={!Object.keys(drafts).length} />}
          label={`Use draft values (${Object.keys(drafts).length})`}
        />
        {Object.keys(drafts).length > 0 && (
          <Button size="small" onClick={() => { setDrafts({}); setUseDraft(false); }}>Discard drafts</Button>
        )}
        <Button variant="contained" onClick={runPreview} disabled={pvLoading}>
          {pvLoading ? <CircularProgress size={20} color="inherit" /> : 'Run Preview'}
        </Button>
        {pv && <Button variant="outlined" onClick={exportPreviewCSV}>Export CSV</Button>}
      </Box>

      {pv && (
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            {pv.exclusions.total} completions in range — excluded: {pv.exclusions.zero} zero-duration (&lt;30s),{' '}
            {pv.exclusions.over8h} over 8h, {pv.exclusions.synthetic} multi-assignment splits;{' '}
            {pv.exclusions.no_standard} in categories with no standard → {pv.exclusions.analyzed} analyzed.
            {pv.draft_applied && ' DRAFT VALUES APPLIED — nothing saved.'}
          </Typography>

          {pv.exclusions.analyzed === 0 ? (
            <Alert severity="info">No analyzable OBDs in this range.</Alert>
          ) : (
            <>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 1 }}>Building</Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead><TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell>Basis</TableCell><TableCell align="right">Lines</TableCell>
                    <TableCell align="right">Std hours (with PF&D)</TableCell><TableCell align="right">Actual hours</TableCell>
                    <TableCell align="right">% to std</TableCell><TableCell align="right">LPH @100% base</TableCell>
                    <TableCell align="right">LPH @standard (PF&D)</TableCell><TableCell align="right">LPH actual</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {basisRows(pv.building).map(([label, b]) => (
                      <TableRow key={label}>
                        <TableCell sx={{ fontWeight: 600 }}>{label}</TableCell>
                        <TableCell align="right">{pv.building.lines.toLocaleString()}</TableCell>
                        <TableCell align="right">{r1(pv.building.standard_hours)}</TableCell>
                        <TableCell align="right">{r1(b.actual_hours)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{pct(b.pf)}</TableCell>
                        <TableCell align="right">{r1(b.lph_base_standard)}</TableCell>
                        <TableCell align="right">{r1(b.lph_with_pfd)}</TableCell>
                        <TableCell align="right">{r1(b.lph_actual)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>By category</Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead><TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell>Category</TableCell><TableCell align="right">OBDs</TableCell><TableCell align="right">Lines</TableCell>
                    <TableCell align="right">Std hours</TableCell><TableCell align="right">Elapsed h</TableCell>
                    <TableCell align="right">% to std (elapsed)</TableCell><TableCell align="right">Median % to std</TableCell>
                    <TableCell align="right">Active h</TableCell><TableCell align="right">% to std (active)</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {pv.byCategory.map((c) => (
                      <TableRow key={c.category}>
                        <TableCell>{c.category}</TableCell>
                        <TableCell align="right">{c.obds.toLocaleString()}</TableCell>
                        <TableCell align="right">{c.lines.toLocaleString()}</TableCell>
                        <TableCell align="right">{r1(c.standard_hours)}</TableCell>
                        <TableCell align="right">{r1(c.elapsed.actual_hours)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>{pct(c.elapsed.pf)}</TableCell>
                        <TableCell align="right">{pct(c.elapsed.median_pf)}</TableCell>
                        <TableCell align="right">{r1(c.active.actual_hours)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>{pct(c.active.pf)}</TableCell>
                      </TableRow>
                    ))}
                    {Object.entries(pv.no_standard_by_category || {}).map(([cat, n]) => (
                      <TableRow key={cat}>
                        <TableCell>{cat}</TableCell>
                        <TableCell align="right">{n}</TableCell>
                        <TableCell colSpan={7} sx={{ color: 'text.secondary', fontStyle: 'italic' }}>No standard</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>By OBD size — the setup-component check</Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead><TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell>Lines</TableCell><TableCell align="right">OBDs</TableCell>
                    <TableCell align="right">% to std (elapsed)</TableCell><TableCell align="right">% to std (active)</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {pv.bySize.map((b) => (
                      <TableRow key={b.bucket}>
                        <TableCell>{b.bucket}</TableCell>
                        <TableCell align="right">{b.obds.toLocaleString()}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>{pct(b.elapsed.pf)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>{pct(b.active.pf)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                By associate <Chip label="INTERNAL — not for distribution" size="small" color="warning" sx={{ ml: 1 }} />
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead><TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell>Associate</TableCell><TableCell align="right">OBDs</TableCell>
                    <TableCell align="right">% to std (el)</TableCell><TableCell align="right">% to std (ac)</TableCell>
                    <TableCell align="right">Median lines</TableCell><TableCell align="right">% small (≤5)</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {pv.byAssociate.map((a) => (
                      <TableRow key={a.associate}>
                        <TableCell>{a.associate}</TableCell>
                        <TableCell align="right">{a.obds.toLocaleString()}</TableCell>
                        <TableCell align="right">{pct(a.elapsed.pf)}</TableCell>
                        <TableCell align="right">{pct(a.active.pf)}</TableCell>
                        <TableCell align="right">{a.median_lines}</TableCell>
                        <TableCell align="right">{a.pct_small}%</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Box>
      )}

      {/* ── Edit dialog ── */}
      <Dialog open={!!edit} onClose={() => setEdit(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{edit?.category} — labor standard</DialogTitle>
        <DialogContent>
          {edit && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              {edit.error && <Alert severity="error">{edit.error}</Alert>}
              <TextField label="Per OBD (minutes — decimals allowed, e.g. 1.5 = 90s)" value={edit.perObdMin}
                onChange={(e) => setEdit({ ...edit, perObdMin: e.target.value, error: '' })} size="small" />
              <TextField label="Per line (seconds)" value={edit.perLineSec}
                onChange={(e) => setEdit({ ...edit, perLineSec: e.target.value, error: '' })} size="small" />
              <TextField label="Per unit (seconds — 0 = unused)" value={edit.perUnitSec}
                onChange={(e) => setEdit({ ...edit, perUnitSec: e.target.value, error: '' })} size="small" />
              <TextField label="PF&D % (share of final standard)" value={edit.pfdPct}
                onChange={(e) => setEdit({ ...edit, pfdPct: e.target.value, error: '' })} size="small" />
              <TextField label="Effective from" type="date" value={edit.effectiveFrom}
                onChange={(e) => setEdit({ ...edit, effectiveFrom: e.target.value, error: '' })}
                size="small" InputLabelProps={{ shrink: true }}
                helperText="Takes effect at 3:30 AM ET (business-day cutoff). Saving closes the current version." />
              <Typography variant="body2" color="text.secondary">
                Example — 10-line OBD:{' '}
                {(() => {
                  const ex = lsExpected((Number(edit.perObdMin) || 0) * 60, edit.perLineSec, edit.perUnitSec, edit.pfdPct, 10, 0);
                  return ex === null ? 'invalid PF&D' : fmtSecs(ex);
                })()}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEdit(null)}>Cancel</Button>
          <Button onClick={applyDraft} color="warning">Apply as draft (preview only)</Button>
          <Button onClick={saveEdit} variant="contained">Save new version</Button>
        </DialogActions>
      </Dialog>

      {/* ── History dialog ── */}
      <Dialog open={!!history} onClose={() => setHistory(null)} maxWidth="md" fullWidth>
        <DialogTitle>{history?.category} — standard history</DialogTitle>
        <DialogContent>
          <Table size="small">
            <TableHead><TableRow>
              <TableCell>Effective from</TableCell><TableCell>Effective to</TableCell>
              <TableCell align="right">Per OBD</TableCell><TableCell align="right">Per line (s)</TableCell>
              <TableCell align="right">Per unit (s)</TableCell><TableCell align="right">PF&D %</TableCell>
              <TableCell>By</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {(history?.rows || []).map((h, i) => (
                <TableRow key={i} sx={{ backgroundColor: h.effective_to ? 'inherit' : '#e8f5e9' }}>
                  <TableCell>{String(h.effective_from).slice(0, 10)}</TableCell>
                  <TableCell>{h.effective_to ? String(h.effective_to).slice(0, 10) : 'current'}</TableCell>
                  <TableCell align="right">{fmtSecs(h.per_obd_seconds)}</TableCell>
                  <TableCell align="right">{Number(h.per_line_seconds)}</TableCell>
                  <TableCell align="right">{Number(h.per_unit_seconds)}</TableCell>
                  <TableCell align="right">{Number(h.pfd_pct)}%</TableCell>
                  <TableCell>{h.updated_by || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions><Button onClick={() => setHistory(null)}>Close</Button></DialogActions>
      </Dialog>

      <Snackbar open={!!snack} autoHideDuration={5000} onClose={() => setSnack(null)} message={snack || ''} />
    </Box>
  );
}

const Configuration = () => {
  const { currentUser, isAdmin, refreshTabVisibility } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  // Store Priority banner: how many auto-added stores await a lead decision
  const [storesNeedPlacement, setStoresNeedPlacement] = useState(0);
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${LS_API}/api/store-priority/summary`, { headers: lsHeaders() });
        const d = await r.json().catch(() => null);
        if (r.ok && d) setStoresNeedPlacement(d.needs_placement || 0);
      } catch { /* banner stays hidden */ }
    })();
  }, [activeTab]);
  
  // Platform list
  // ATL is split into two sub-platforms for different departure times
  const platforms = ['ASH', 'ATL A&B', 'ATL E/T/ATR', 'BNA', 'CAE', 'GSO', 'MEM', 'PNS', 'RDU', 'SAV', 'TYS'];
  
  // User Management State — real backend accounts (/api/auth/users).
  // Creatable roles = the register whitelist (admins are not creatable here).
  const USER_ROLES = ['associate', 'manager', 'report', 'display'];
  const [users, setUsers] = useState([]);
  const [userDialog, setUserDialog] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [newFullName, setNewFullName] = useState('');
  const [newRole, setNewRole] = useState('associate');

  // Shift Management State
  const [shifts, setShifts] = useState([]);
  const [shiftDialog, setShiftDialog] = useState(false);
  const [newShift, setNewShift] = useState({
    name: '',
    startTime: '07:00',
    endTime: '15:00',
    associates: []
  });

  // Delivery Schedule State
  const [deliverySchedule, setDeliverySchedule] = useState({});
  
  // Dispatch Time State
  const [dispatchTimes, setDispatchTimes] = useState({});
  
  // Target Arrival Time State
  const [targetArrivalTimes, setTargetArrivalTimes] = useState({});
  
  // Delivery Cost Configuration State
  const [deliveryCostConfig, setDeliveryCostConfig] = useState({
    warehouseWageRate: 20.00,
    platformWageRate: 18.00,
    platformSettings: {}
  });

  // Backroom Space Configuration State
  const [backroomSpace, setBackroomSpace] = useState({});

  // KPI Goals State
  const [kpiGoals, setKPIGoals] = useState({});

  // Manual Override State
  const [manualOverrides, setManualOverrides] = useState({});
  const [editingKPI, setEditingKPI] = useState(null);
  const [showOverrideWarning, setShowOverrideWarning] = useState(false);
  const [pendingKPIEdit, setPendingKPIEdit] = useState(null);

  // KPI Values State (for displaying current values)
  const [kpiValues, setKpiValues] = useState({});
  
  // Loading states
  const [isLoadingGoals, setIsLoadingGoals] = useState(true);
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);

  const [expandedPlatforms, setExpandedPlatforms] = useState({ 'BNA': true }); // BNA expanded by default
  
  // System Settings State
  const [systemSettings, setSystemSettings] = useState({
    companyName: 'Paradies',
    warehouseLocation: 'Atlanta, GA',
    targetUnitsPerHour: 50,
    targetLinesPerHour: 20,
    targetAccuracy: 95
  });

  // Employee Management State
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [employeeDialog, setEmployeeDialog] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [employeeForm, setEmployeeForm] = useState({ name: '', employee_id: '' });
  const [employeeError, setEmployeeError] = useState('');

  // SKU Categories State (tab 5)
  const SKU_PULSE_CATEGORIES = [
    'CANDY', 'BEVERAGES', 'SNACKS', 'HBA', 'ELECTRONICS', 'PREMIUM ELECTRONICS',
    'TRAVEL', 'DRINKWARE', 'TOYS', 'GIFTS', 'APPAREL',
    'LICENSED', 'SPORTS', 'BOOKS', 'TOBACCO', 'EYEWEAR',
  ];
  const [unknownSkus, setUnknownSkus] = useState([]);
  const [loadingUnknownSkus, setLoadingUnknownSkus] = useState(false);
  const [skuSelectedCategory, setSkuSelectedCategory] = useState({});
  const [assigningSkuMaterial, setAssigningSkuMaterial] = useState(null);
  const [skuAssignSuccess, setSkuAssignSuccess] = useState(null);
  const [skuError, setSkuError] = useState(null);
  const [skuOverrides, setSkuOverrides] = useState([]);
  const [loadingSkuOverrides, setLoadingSkuOverrides] = useState(false);
  const [skuOverridesError, setSkuOverridesError] = useState(null);
  const [skuDeleteTarget, setSkuDeleteTarget] = useState(null);
  const [skuDeleting, setSkuDeleting] = useState(false);
  const [skuSubTab, setSkuSubTab] = useState(0); // 0 = Unknown, 1 = Overrides

  // SKU Inventory State (tab 6) — read-only view of sku_inventory
  const SKU_INV_PAGE_SIZE = 100;
  const [skuInv, setSkuInv] = useState([]);
  const [skuInvLoading, setSkuInvLoading] = useState(false);
  const [skuInvError, setSkuInvError] = useState(null);
  const [skuInvSearch, setSkuInvSearch] = useState('');
  const [skuInvSort, setSkuInvSort] = useState('last_seen_date_desc');
  const [skuInvPage, setSkuInvPage] = useState(0);
  const [skuInvTotal, setSkuInvTotal] = useState(0);

  // Ship-To Management State (tab 7)
  // Stub pattern mirrors backend logic: 3-uppercase-letters + dash + digits, e.g. MEM-1211
  const SHIP_TO_STUB_REGEX = /^[A-Z]{3}-\d+$/;
  const isShipToStub = (name) => !name || name.trim() === '' || SHIP_TO_STUB_REGEX.test(name.trim());

  const [shipTos, setShipTos] = useState([]);           // all rows from API
  const [loadingShipTos, setLoadingShipTos] = useState(false);
  const [shipToError, setShipToError] = useState(null);
  const [shipToSearch, setShipToSearch] = useState('');
  // Per-row editing state: { [ship_to_number]: draftName }
  const [shipToEdits, setShipToEdits] = useState({});
  const [savingShipTo, setSavingShipTo] = useState(null); // ship_to_number currently saving
  // Snackbar for Ship-To saves
  const [shipToSnack, setShipToSnack] = useState({ open: false, message: '', severity: 'success' });

  // Tab Visibility (tab 7)
  // tabVisMatrix: { role: { tabKey: bool } } — source of truth loaded from server
  // tabVisDraft:  same shape — edited copy, written on toggle, sent on save
  const [tabVisMatrix, setTabVisMatrix] = useState({});
  const [tabVisDraft, setTabVisDraft] = useState({});
  const [loadingTabVis, setLoadingTabVis] = useState(false);
  const [savingTabVis, setSavingTabVis] = useState(false);
  const [tabVisSnack, setTabVisSnack] = useState({ open: false, message: '', severity: 'success' });
  const [tabVisWarnDialog, setTabVisWarnDialog] = useState({ open: false, role: null, tabKey: null, label: '' });

  // Load configuration data from API
  useEffect(() => {
    const loadConfigurationData = async () => {
      try {
        console.log('Loading configuration data from API...');
        
        // Load all configuration data from API
        const [shiftsData, scheduleData, dispatchData, arrivalData, costData, spaceData, settingsData] = await Promise.allSettled([
          api.getConfig('shifts'),
          fetch(`${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5001'}/api/delivery-schedules`).then(res => res.json()),
          api.getConfig('dispatchTimes'),
          api.getConfig('targetArrivalTimes'),
          api.getConfig('deliveryCostConfig'),
          api.getConfig('backroomSpace'),
          api.getConfig('systemSettings')
        ]);

        // Handle shifts data
        if (shiftsData.status === 'fulfilled' && shiftsData.value) {
          setShifts(shiftsData.value);
          console.log('Loaded shifts from API:', shiftsData.value);
        }
        
        // Handle delivery schedule from database
        // Also extract departure times from the schedule JSON
        if (scheduleData.status === 'fulfilled' && Array.isArray(scheduleData.value)) {
          const scheduleObj = {};
          const extractedDispatchTimes = {};

          // Get today's day name for extracting current departure time
          const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
          const today = dayNames[new Date().getDay()];

          scheduleData.value.forEach(item => {
            // Handle the schedule data - could be old array format or new object format
            const scheduleData = item.schedule;

            if (scheduleData && typeof scheduleData === 'object' && !Array.isArray(scheduleData)) {
              // New format: {"Monday": {"deliveryDay": true, "departureTime": "04:35"}, ...}
              // Convert to skip days (days where deliveryDay is false)
              const skipDays = [];
              const dayToNum = { 'Sunday': 0, 'Monday': 1, 'Tuesday': 2, 'Wednesday': 3, 'Thursday': 4, 'Friday': 5, 'Saturday': 6 };

              Object.entries(scheduleData).forEach(([dayName, dayInfo]) => {
                if (!dayInfo.deliveryDay) {
                  skipDays.push(dayToNum[dayName]);
                }
              });
              scheduleObj[item.platform] = skipDays;

              // Extract departure time for today (or first available day with a time)
              if (scheduleData[today] && scheduleData[today].departureTime) {
                // Convert 24hr to 12hr format
                const time24 = scheduleData[today].departureTime;
                const [hours, minutes] = time24.split(':').map(Number);
                const period = hours >= 12 ? 'PM' : 'AM';
                const hours12 = hours % 12 || 12;
                extractedDispatchTimes[item.platform] = `${hours12}:${String(minutes).padStart(2, '0')} ${period}`;
              } else {
                // Find first day with a departure time
                for (const dayName of dayNames) {
                  if (scheduleData[dayName] && scheduleData[dayName].departureTime) {
                    const time24 = scheduleData[dayName].departureTime;
                    const [hours, minutes] = time24.split(':').map(Number);
                    const period = hours >= 12 ? 'PM' : 'AM';
                    const hours12 = hours % 12 || 12;
                    extractedDispatchTimes[item.platform] = `${hours12}:${String(minutes).padStart(2, '0')} ${period}`;
                    break;
                  }
                }
              }
            } else if (Array.isArray(scheduleData)) {
              // Old format: just an array of delivery days
              const skipDays = [0, 1, 2, 3, 4, 5, 6].filter(day => !scheduleData.includes(day));
              scheduleObj[item.platform] = skipDays;
            }
          });

          setDeliverySchedule(scheduleObj);
          console.log('✅ Loaded delivery schedules from database:', scheduleObj);

          // Set dispatch times from the schedule data if we extracted any
          if (Object.keys(extractedDispatchTimes).length > 0) {
            // Merge with defaults for any missing platforms
            const mergedDispatchTimes = {};
            platforms.forEach(platform => {
              mergedDispatchTimes[platform] = extractedDispatchTimes[platform] || '5:00 PM';
            });
            setDispatchTimes(mergedDispatchTimes);
            console.log('✅ Loaded dispatch times from delivery schedules:', mergedDispatchTimes);
          } else {
            // Fall back to config API or defaults
            if (dispatchData.status === 'fulfilled' && dispatchData.value) {
              setDispatchTimes(dispatchData.value);
              console.log('Loaded dispatch times from config API:', dispatchData.value);
            } else {
              const defaultTimes = {};
              platforms.forEach(platform => {
                defaultTimes[platform] = '5:00 PM';
              });
              setDispatchTimes(defaultTimes);
            }
          }
        } else {
          console.warn('⚠️ Failed to load delivery schedules from database, using defaults');

          // Handle dispatch times from config API as fallback
          if (dispatchData.status === 'fulfilled' && dispatchData.value) {
            setDispatchTimes(dispatchData.value);
            console.log('Loaded dispatch times from config API:', dispatchData.value);
          } else {
            // Set default dispatch times
            const defaultTimes = {};
            platforms.forEach(platform => {
              defaultTimes[platform] = '5:00 PM';
            });
            setDispatchTimes(defaultTimes);
          }
        }

        // Handle target arrival times
        if (arrivalData.status === 'fulfilled' && arrivalData.value) {
          setTargetArrivalTimes(arrivalData.value);
          console.log('Loaded target arrival times from API:', arrivalData.value);
        } else {
          // Set default target arrival times
          const defaultArrivalTimes = {};
          platforms.forEach(platform => {
            defaultArrivalTimes[platform] = '9:00 AM';
          });
          setTargetArrivalTimes(defaultArrivalTimes);
        }
        
        // Handle delivery cost config
        if (costData.status === 'fulfilled' && costData.value) {
          setDeliveryCostConfig(costData.value);
          console.log('Loaded delivery cost config from API:', costData.value);
        } else {
          // Set default platform-specific settings
          const defaultPlatformSettings = {};
          platforms.forEach(platform => {
            defaultPlatformSettings[platform] = {
              pickingHours: 4.0,
              putawayHours: 3.0,
              transportationCost: 500.00
            };
          });
          setDeliveryCostConfig(prev => ({
            ...prev,
            platformSettings: defaultPlatformSettings
          }));
        }
        
        // Handle backroom space
        if (spaceData.status === 'fulfilled' && spaceData.value) {
          setBackroomSpace(spaceData.value);
          console.log('Loaded backroom space from API:', spaceData.value);
        } else {
          // Set default with example BNA store
          const defaultBackroomSpace = {
            'BNA-0157': 1415
          };
          setBackroomSpace(defaultBackroomSpace);
        }
        
        // Handle system settings
        if (settingsData.status === 'fulfilled' && settingsData.value) {
          setSystemSettings(settingsData.value);
          console.log('Loaded system settings from API');
        }
        
      } catch (error) {
        console.error('Error loading configuration data:', error);
      } finally {
        setIsLoadingConfig(false);
      }
    };

    loadConfigurationData();
  }, []);

  // Save configuration data to API
  useEffect(() => {
    if (shifts.length > 0 && !isLoadingConfig) {
      api.updateConfig('shifts', shifts)
        .then(() => console.log('Saved shifts to API'))
        .catch(error => console.error('Error saving shifts:', error));
    }
  }, [shifts, isLoadingConfig]);

  // Save both delivery schedule and dispatch times together to maintain consistency
  useEffect(() => {
    if ((Object.keys(deliverySchedule).length > 0 || Object.keys(dispatchTimes).length > 0) && !isLoadingConfig) {
      // Save delivery schedules AND dispatch times to database
      const saveSchedulesToDatabase = async () => {
        try {
          console.log('💾 Saving delivery schedules and dispatch times to database...');

          // Helper to convert 12hr time to 24hr
          const convertTo24HourFormat = (time12) => {
            if (!time12) return null;
            const match = time12.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
            if (!match) return null;
            let [, hours, minutes, period] = match;
            hours = parseInt(hours);
            if (period.toUpperCase() === 'PM' && hours !== 12) hours += 12;
            if (period.toUpperCase() === 'AM' && hours === 12) hours = 0;
            return `${String(hours).padStart(2, '0')}:${minutes}`;
          };

          const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
          const dayNumToName = { 0: 'Sunday', 1: 'Monday', 2: 'Tuesday', 3: 'Wednesday', 4: 'Thursday', 5: 'Friday', 6: 'Saturday' };

          // Get all platforms from either deliverySchedule or dispatchTimes
          const allPlatforms = [...new Set([...Object.keys(deliverySchedule), ...Object.keys(dispatchTimes)])];

          const savePromises = allPlatforms.map((platform) => {
            const skipDays = deliverySchedule[platform] || [];
            const dispatchTime = dispatchTimes[platform];
            const dispatchTime24 = convertTo24HourFormat(dispatchTime);

            // Build the new schedule format: {"Monday": {"deliveryDay": true, "departureTime": "04:35"}, ...}
            const scheduleObj = {};
            dayNames.forEach((dayName, idx) => {
              const isDeliveryDay = !skipDays.includes(idx);
              scheduleObj[dayName] = {
                deliveryDay: isDeliveryDay,
                departureTime: isDeliveryDay ? dispatchTime24 : null
              };
            });

            const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5001';
            return fetch(`${API_BASE}/api/delivery-schedules`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                platform,
                schedule: scheduleObj,
                trafficPattern: 'mixed',
                notes: `Updated via Configuration tab`,
                updatedBy: 'Config User'
              })
            });
          });

          await Promise.all(savePromises);
          console.log('✅ All delivery schedules and dispatch times saved to database');
        } catch (error) {
          console.error('❌ Error saving delivery schedules:', error);
        }
      };

      saveSchedulesToDatabase();
    }
  }, [deliverySchedule, dispatchTimes, isLoadingConfig]);

  useEffect(() => {
    if (Object.keys(targetArrivalTimes).length > 0 && !isLoadingConfig) {
      api.updateConfig('targetArrivalTimes', targetArrivalTimes)
        .then(() => console.log('Saved target arrival times to API:', targetArrivalTimes))
        .catch(error => console.error('Error saving target arrival times:', error));
    }
  }, [targetArrivalTimes, isLoadingConfig]);

  useEffect(() => {
    if (Object.keys(deliveryCostConfig.platformSettings || {}).length > 0 && !isLoadingConfig) {
      api.updateConfig('deliveryCostConfig', deliveryCostConfig)
        .then(() => console.log('Saved delivery cost config to API:', deliveryCostConfig))
        .catch(error => console.error('Error saving delivery cost config:', error));
    }
  }, [deliveryCostConfig, isLoadingConfig]);

  useEffect(() => {
    if (!isLoadingConfig) {
      api.updateConfig('backroomSpace', backroomSpace)
        .then(() => console.log('Saved backroom space config to API'))
        .catch(error => console.error('Error saving backroom space:', error));
    }
  }, [backroomSpace, isLoadingConfig]);

  useEffect(() => {
    if (!isLoadingConfig) {
      api.updateConfig('systemSettings', systemSettings)
        .then(() => console.log('Saved system settings to API'))
        .catch(error => console.error('Error saving system settings:', error));
    }
  }, [systemSettings, isLoadingConfig]);

  // Load KPI goals from API on mount
  useEffect(() => {
    const loadKPIGoals = async () => {
      try {
        const data = await api.getKpiGoals();
        console.log('🎯 Loaded KPI goals from API:', data);
        
        // Convert snake_case to camelCase
        const formattedGoals = {
          vendorFillRate: data.vendor_fill_rate,
          skuInStock: data.sku_in_stock,
          onTimeDelivery: data.on_time_delivery,
          vendorsConvertedSales: data.vendors_converted_sales,
          vendorsConvertedCount: data.vendors_converted_count,
          dockToStock: data.dock_to_stock,
          lphPicking: data.lph_picking,
          pickAccuracy: data.pick_accuracy,
          outboundServiceLevel: data.outbound_service_level,
          inventoryAccuracy: data.inventory_accuracy,
          storageUtilization: data.storage_utilization,
          automationUptime: data.automation_uptime,
          fourWallCost: data.four_wall_cost,
          receivingUph: data.receiving_uph,
          complete4hrs: data.complete_4hrs,
          inventoryAccuracyPlatform: data.inventory_accuracy_platform,
          skuInStockPlatform: data.sku_in_stock_platform,
          scheduleScore: data.schedule_score,
          deliveryCost: data.delivery_cost,
          totalCombinedCost: data.total_combined_cost,
          costCompareBase: data.cost_compare_base,
          varianceBudget: data.variance_budget,
          percentTotalSales: data.percent_total_sales
        };
        
        setKPIGoals(formattedGoals);
      } catch (error) {
        console.error('Error loading KPI goals:', error);
      } finally {
        setIsLoadingGoals(false);
      }
    };

    loadKPIGoals();
  }, []);

  // Load manual overrides and KPI values from API on mount
  useEffect(() => {
    const loadManualData = async () => {
      try {
        // Load manual overrides from API
        const overridesData = await api.getManualOverrides();
        if (overridesData) {
          setManualOverrides(overridesData);
          console.log('✅ Manual overrides loaded from API:', overridesData);
        }

        // Load current KPI values from API
        const kpiData = await api.getKpiValues();
        if (kpiData) {
          // Convert snake_case to camelCase for ALL KPI fields
          const formattedKpiValues = {
            vendorFillRate: kpiData.vendor_fill_rate,
            currentWeek: kpiData.current_week,
            warehouseInStock: kpiData.warehouse_in_stock,
            totalSkuCount: kpiData.total_sku_count,
            onTimeDelivery: kpiData.on_time_delivery,
            totalDeliveries: kpiData.total_deliveries,
            // Add ALL other KPI fields
            lphPicking: kpiData.lph_picking,
            pickAccuracy: kpiData.pick_accuracy,
            outboundServiceLevel: kpiData.outbound_service_level,
            inventoryAccuracy: kpiData.inventory_accuracy,
            vendorsConvertedSales: kpiData.vendors_converted_sales,
            vendorsConvertedCount: kpiData.vendors_converted_count,
            dockToStock: kpiData.dock_to_stock,
            storageUtilization: kpiData.storage_utilization,
            automationUptime: kpiData.automation_uptime,
            fourWallCost: kpiData.four_wall_cost,
            receivingUph: kpiData.receiving_uph,
            complete4hrs: kpiData.complete_4hrs,
            inventoryAccuracyPlatform: kpiData.inventory_accuracy_platform,
            skuInStockPlatform: kpiData.sku_in_stock_platform,
            scheduleScore: kpiData.schedule_score,
            deliveryCost: kpiData.delivery_cost,
            totalCombinedCost: kpiData.total_combined_cost,
            costCompareBase: kpiData.cost_compare_base,
            varianceBudget: kpiData.variance_budget,
            percentTotalSales: kpiData.percent_total_sales
          };
          setKpiValues(formattedKpiValues);
          console.log('✅ KPI values loaded from API:', formattedKpiValues);
        }
      } catch (error) {
        console.error('❌ Error loading manual data:', error);
      }
    };

    loadManualData();
  }, []);

  // KPI Goals Functions
  const handleKPIGoalChange = async (key, value) => {
    const updatedGoals = {
      ...kpiGoals,
      [key]: value
    };
    setKPIGoals(updatedGoals);
    
    try {
      // Convert camelCase to snake_case for API
      let snakeCaseKey;
      if (key === 'complete4hrs') {
        snakeCaseKey = 'complete_4hrs'; // Special case mapping
      } else {
        snakeCaseKey = key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
      }
      await api.updateKpiGoals({ [snakeCaseKey]: value });
      console.log('💾 KPI goal saved:', key, value);
    } catch (error) {
      console.error('Error saving KPI goal:', error);
    }
  };

  const handleSaveKPIGoals = () => {
    // Data is already saved via handleKPIGoalChange
    alert('✅ KPI Goals are automatically saved!');
  };

  // Manual Override Functions
  const handleEditKPI = (kpiKey, kpiLabel) => {
    setPendingKPIEdit({ key: kpiKey, label: kpiLabel });
    setShowOverrideWarning(true);
  };

  const confirmOverride = () => {
    setEditingKPI(pendingKPIEdit.key);
    setShowOverrideWarning(false);
  };

  const cancelOverride = () => {
    setPendingKPIEdit(null);
    setShowOverrideWarning(false);
  };

  const saveManualOverride = async (kpiKey, value) => {
    try {
      // Save manual override via API
      await api.saveManualOverride(kpiKey, value, 'Manual Entry');
      
      const updated = {
        ...manualOverrides,
        [kpiKey]: {
          value: value,
          timestamp: new Date().toISOString(),
          overriddenBy: 'Manual Entry'
        }
      };
      setManualOverrides(updated);
      
      // Update KPI values as well
      const updatedKPIValues = {
        ...kpiValues,
        [kpiKey]: value
      };
      setKpiValues(updatedKPIValues);
      
      // Save updated KPI values to API
      await api.updateKpiValues({
        vendor_fill_rate: updatedKPIValues.vendorFillRate,
        current_week: updatedKPIValues.currentWeek,
        warehouse_in_stock: updatedKPIValues.warehouseInStock,
        total_sku_count: updatedKPIValues.totalSkuCount,
        on_time_delivery: updatedKPIValues.onTimeDelivery,
        total_deliveries: updatedKPIValues.totalDeliveries
      });
      
      setEditingKPI(null);
      console.log('💾 Manual override saved for', kpiKey, ':', value);
    } catch (error) {
      console.error('Error saving manual override:', error);
    }
  };

  const clearOverride = async (kpiKey) => {
    try {
      // Clear manual override via API
      await api.deleteManualOverride(kpiKey);
      
      const updated = { ...manualOverrides };
      delete updated[kpiKey];
      setManualOverrides(updated);
      
      // Remove from KPI values as well
      const updatedKPIValues = { ...kpiValues };
      delete updatedKPIValues[kpiKey];
      setKpiValues(updatedKPIValues);
      
      // Update KPI values in API
      await api.updateKpiValues({
        vendor_fill_rate: updatedKPIValues.vendorFillRate,
        current_week: updatedKPIValues.currentWeek,
        warehouse_in_stock: updatedKPIValues.warehouseInStock,
        total_sku_count: updatedKPIValues.totalSkuCount,
        on_time_delivery: updatedKPIValues.onTimeDelivery,
        total_deliveries: updatedKPIValues.totalDeliveries
      });
      
      console.log('🗑️ Manual override cleared for', kpiKey);
    } catch (error) {
      console.error('Error clearing manual override:', error);
    }
  };

  // User Management Functions — real backend accounts. The generated
  // password is shown ONCE in the alert and never stored client-side.
  const loadUsers = useCallback(async () => {
    try {
      const res = await fetch(`${LS_API}/api/auth/users`, { headers: lsHeaders() });
      const data = await res.json();
      if (res.ok && Array.isArray(data)) setUsers(data);
    } catch { /* table just stays empty */ }
  }, []);
  useEffect(() => { loadUsers(); }, [loadUsers]);

  const genPassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    let p = '';
    const buf = new Uint32Array(12);
    window.crypto.getRandomValues(buf);
    for (const b of buf) p += chars[b % chars.length];
    return p;
  };

  const handleAddUser = async () => {
    if (!newUsername.trim()) {
      alert('Please enter a username');
      return;
    }
    const password = genPassword();
    try {
      const res = await fetch(`${LS_API}/api/auth/register`, {
        method: 'POST', headers: lsHeaders(),
        body: JSON.stringify({ username: newUsername.trim(), password, role: newRole, fullName: newFullName.trim() || undefined }),
      });
      const data = await res.json();
      if (!res.ok) { alert(data.error || 'Failed to create user'); return; }
      alert(`User created successfully!\nUsername: ${data.username}\nPassword: ${password}\n\nSave this password now — it is not shown again.`);
      setUserDialog(false);
      setNewUsername('');
      setNewFullName('');
      setNewRole('associate');
      loadUsers();
    } catch {
      alert('Failed to create user');
    }
  };

  const handleResetPassword = async (userId) => {
    if (!window.confirm('Reset this user\'s password? Their current sessions are signed out.')) return;
    try {
      const res = await fetch(`${LS_API}/api/auth/users/${userId}/reset-password`, {
        method: 'POST', headers: lsHeaders(),
      });
      const data = await res.json();
      if (!res.ok) { alert(data.error || 'Failed to reset password'); return; }
      alert(`Password reset for ${data.username}.\nNew password: ${data.password}\n\nSave this password now — it is not shown again.`);
    } catch {
      alert('Failed to reset password');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      const res = await fetch(`${LS_API}/api/auth/users/${userId}`, {
        method: 'DELETE', headers: lsHeaders(),
      });
      const data = await res.json();
      if (!res.ok) { alert(data.error || 'Failed to delete user'); return; }
      loadUsers();
    } catch {
      alert('Failed to delete user');
    }
  };

  // Shift Management Functions
  const handleAddShift = () => {
    setShifts(prev => [...prev, { ...newShift, id: Date.now().toString() }]);
    setShiftDialog(false);
    setNewShift({
      name: '',
      startTime: '07:00',
      endTime: '15:00',
      associates: []
    });
  };

  const handleDeleteShift = (shiftId) => {
    if (window.confirm('Are you sure you want to delete this shift?')) {
      setShifts(prev => prev.filter(s => s.id !== shiftId));
    }
  };

  // Employee Management Functions
  const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5001';

  const fetchEmployees = async () => {
    setLoadingEmployees(true);
    try {
      // Fetch ALL workers including inactive ones for management
      // (endpoint requires auth — roster is PII + kiosk credential)
      const response = await fetch(`${API_BASE}/api/obd/workers?all=true`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setEmployees(data);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    } finally {
      setLoadingEmployees(false);
    }
  };

  // Load employees when tab 4 (Employee Management) is selected
  useEffect(() => {
    if (activeTab === 4) {
      fetchEmployees();
    }
  }, [activeTab]);

  // ── SKU Categories (tab 5) ─────────────────────────────────────────────────
  const getSkuAuthHeaders = () => {
    const token = localStorage.getItem('authToken');
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  };

  const fetchUnknownSkus = useCallback(async () => {
    setLoadingUnknownSkus(true);
    setSkuError(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/unknown-skus`, { headers: getSkuAuthHeaders() });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setUnknownSkus(data);
      // Pre-select the recommended category; '' means no recommendation (force user to choose)
      const defaults = {};
      data.forEach((row) => { defaults[row.material] = row.recommended_category || ''; });
      setSkuSelectedCategory((prev) => ({ ...defaults, ...prev }));
    } catch (err) {
      setSkuError('Failed to load unknown SKUs: ' + err.message);
    } finally {
      setLoadingUnknownSkus(false);
    }
  }, []);

  const fetchSkuOverrides = useCallback(async () => {
    setLoadingSkuOverrides(true);
    setSkuOverridesError(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/sku-overrides`, { headers: getSkuAuthHeaders() });
      if (!res.ok) throw new Error(await res.text());
      setSkuOverrides(await res.json());
    } catch (err) {
      setSkuOverridesError('Failed to load overrides: ' + err.message);
    } finally {
      setLoadingSkuOverrides(false);
    }
  }, []);

  // Load SKU data when tab 5 is selected
  useEffect(() => {
    if (activeTab === 5) {
      fetchUnknownSkus();
      fetchSkuOverrides();
    }
  }, [activeTab]);

  const handleSkuAssign = async (material) => {
    const pulse_category = skuSelectedCategory[material];
    if (!pulse_category) return;
    setAssigningSkuMaterial(material);
    try {
      const res = await fetch(`${API_BASE}/api/admin/sku-override`, {
        method: 'POST',
        headers: getSkuAuthHeaders(),
        body: JSON.stringify({ material, pulse_category, reason: 'Manual admin assignment' }),
      });
      if (!res.ok) throw new Error(await res.text());
      setSkuAssignSuccess(material);
      setTimeout(() => setSkuAssignSuccess(null), 2000);
      setUnknownSkus((prev) => prev.filter((r) => r.material !== material));
      fetchSkuOverrides();
    } catch (err) {
      setSkuError('Failed to assign override: ' + err.message);
    } finally {
      setAssigningSkuMaterial(null);
    }
  };

  const handleSkuDeleteConfirm = async () => {
    if (!skuDeleteTarget) return;
    setSkuDeleting(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/sku-override/${encodeURIComponent(skuDeleteTarget)}`, {
        method: 'DELETE',
        headers: getSkuAuthHeaders(),
      });
      if (!res.ok) throw new Error(await res.text());
      setSkuOverrides((prev) => prev.filter((r) => r.material !== skuDeleteTarget));
      setSkuDeleteTarget(null);
      fetchUnknownSkus();
    } catch (err) {
      setSkuOverridesError('Failed to delete override: ' + err.message);
    } finally {
      setSkuDeleting(false);
    }
  };

  // ── SKU Inventory (tab 6) ─────────────────────────────────────────────────
  const fetchSkuInventory = useCallback(async (search, sort, page) => {
    setSkuInvLoading(true);
    setSkuInvError(null);
    try {
      const params = new URLSearchParams({
        sort,
        limit: String(SKU_INV_PAGE_SIZE),
        offset: String(page * SKU_INV_PAGE_SIZE),
      });
      if (search) params.set('search', search);
      const res = await fetch(`${API_BASE}/api/admin/sku-inventory?${params.toString()}`, {
        headers: getSkuAuthHeaders(),
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setSkuInv(data.rows || []);
      setSkuInvTotal(data.total || 0);
    } catch (err) {
      setSkuInvError('Failed to load SKU inventory: ' + err.message);
    } finally {
      setSkuInvLoading(false);
    }
  }, []);

  // Reset to page 0 when search or sort changes
  useEffect(() => {
    if (activeTab !== 6) return;
    setSkuInvPage(0);
  }, [skuInvSearch, skuInvSort, activeTab]);

  // Debounced fetch on tab + search + sort + page
  useEffect(() => {
    if (activeTab !== 6) return;
    const handle = setTimeout(() => {
      fetchSkuInventory(skuInvSearch.trim(), skuInvSort, skuInvPage);
    }, 300);
    return () => clearTimeout(handle);
  }, [activeTab, skuInvSearch, skuInvSort, skuInvPage, fetchSkuInventory]);

  // ── Ship-To Management (tab 7) ────────────────────────────────────────────
  const fetchShipTos = useCallback(async () => {
    setLoadingShipTos(true);
    setShipToError(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/ship-tos`, { headers: getSkuAuthHeaders() });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setShipTos(data);
      // Initialise draft edits with current names (so inputs are pre-populated)
      const drafts = {};
      data.forEach((row) => { drafts[row.ship_to_number] = row.name || ''; });
      setShipToEdits(drafts);
    } catch (err) {
      setShipToError('Failed to load Ship-Tos: ' + err.message);
    } finally {
      setLoadingShipTos(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 7) fetchShipTos();
    if (activeTab === 8) fetchTabVis();
  }, [activeTab]);

  // Tab Visibility helpers ────────────────────────────────────────────────────

  const fetchTabVis = useCallback(async () => {
    setLoadingTabVis(true);
    try {
      const res = await fetch(`${API_BASE}/api/tab-visibility/all`, { headers: getSkuAuthHeaders() });
      if (!res.ok) throw new Error(await res.text());
      const rows = await res.json();
      // Convert flat array → { role: { tabKey: bool } }
      const matrix = {};
      rows.forEach(({ role, tab_key, visible }) => {
        if (!matrix[role]) matrix[role] = {};
        matrix[role][tab_key] = visible;
      });
      setTabVisMatrix(matrix);
      setTabVisDraft(JSON.parse(JSON.stringify(matrix)));
    } catch (err) {
      setTabVisSnack({ open: true, message: 'Failed to load tab visibility: ' + err.message, severity: 'error' });
    } finally {
      setLoadingTabVis(false);
    }
  }, [API_BASE]);

  const handleTabVisToggle = (role, tabKey, label) => {
    const currentVal = tabVisDraft[role]?.[tabKey] ?? true;
    if (currentVal) {
      // Unchecking — show warning first
      setTabVisWarnDialog({ open: true, role, tabKey, label });
    } else {
      // Re-enabling — no warning needed
      setTabVisDraft(prev => ({
        ...prev,
        [role]: { ...(prev[role] || {}), [tabKey]: true },
      }));
    }
  };

  const confirmTabVisHide = () => {
    const { role, tabKey } = tabVisWarnDialog;
    setTabVisDraft(prev => ({
      ...prev,
      [role]: { ...(prev[role] || {}), [tabKey]: false },
    }));
    setTabVisWarnDialog({ open: false, role: null, tabKey: null, label: '' });
  };

  const handleTabVisSave = async () => {
    setSavingTabVis(true);
    // Flatten draft back to array for PUT
    const updates = [];
    Object.entries(tabVisDraft).forEach(([role, keys]) => {
      Object.entries(keys).forEach(([tab_key, visible]) => {
        updates.push({ role, tab_key, visible });
      });
    });
    try {
      const res = await fetch(`${API_BASE}/api/tab-visibility`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getSkuAuthHeaders() },
        body: JSON.stringify(updates),
      });
      if (!res.ok) throw new Error(await res.text());
      setTabVisMatrix(JSON.parse(JSON.stringify(tabVisDraft)));
      refreshTabVisibility();
      setTabVisSnack({ open: true, message: 'Tab visibility saved.', severity: 'success' });
    } catch (err) {
      setTabVisSnack({ open: true, message: 'Save failed: ' + err.message, severity: 'error' });
    } finally {
      setSavingTabVis(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────

  const handleShipToSave = async (shipToNumber) => {
    const newName = (shipToEdits[shipToNumber] || '').trim();
    if (!newName) {
      setShipToSnack({ open: true, message: 'Name cannot be empty.', severity: 'warning' });
      return;
    }
    setSavingShipTo(shipToNumber);
    try {
      const res = await fetch(`${API_BASE}/api/admin/ship-tos/${encodeURIComponent(shipToNumber)}`, {
        method: 'PATCH',
        headers: getSkuAuthHeaders(),
        body: JSON.stringify({ name: newName }),
      });
      if (!res.ok) throw new Error(await res.text());
      const { row } = await res.json();
      // Update local state optimistically
      setShipTos((prev) =>
        prev.map((r) =>
          r.ship_to_number === shipToNumber
            ? { ...r, name: row.store_name, updated_by: row.updated_by, updated_at: row.updated_at, needs_name: isShipToStub(row.store_name) }
            : r
        )
      );
      setShipToSnack({ open: true, message: `Ship-To ${shipToNumber} saved.`, severity: 'success' });
    } catch (err) {
      // Revert the draft on error
      setShipToEdits((prev) => ({
        ...prev,
        [shipToNumber]: shipTos.find((r) => r.ship_to_number === shipToNumber)?.name || '',
      }));
      setShipToSnack({ open: true, message: `Save failed: ${err.message}`, severity: 'error' });
    } finally {
      setSavingShipTo(null);
    }
  };

  const handleOpenEmployeeDialog = (employee = null) => {
    if (employee) {
      setEditingEmployee(employee);
      setEmployeeForm({ name: employee.name, employee_id: employee.employee_id || '' });
    } else {
      setEditingEmployee(null);
      setEmployeeForm({ name: '', employee_id: '' });
    }
    setEmployeeError('');
    setEmployeeDialog(true);
  };

  const handleSaveEmployee = async () => {
    if (!employeeForm.name.trim()) {
      setEmployeeError('Name is required');
      return;
    }
    if (!employeeForm.employee_id.trim()) {
      setEmployeeError('Employee ID is required');
      return;
    }

    try {
      if (editingEmployee) {
        // Update existing employee
        const response = await fetch(`${API_BASE}/api/obd/workers/${editingEmployee.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('authToken')}` },
          body: JSON.stringify({
            name: employeeForm.name.trim(),
            employee_id: employeeForm.employee_id.trim()
          })
        });

        if (!response.ok) {
          const result = await response.json();
          throw new Error(result.error || 'Failed to update employee');
        }
      } else {
        // Add new employee
        const response = await fetch(`${API_BASE}/api/obd/workers`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('authToken')}` },
          body: JSON.stringify({
            name: employeeForm.name.trim(),
            employee_id: employeeForm.employee_id.trim()
          })
        });

        if (!response.ok) {
          const result = await response.json();
          throw new Error(result.error || 'Failed to add employee');
        }
      }

      setEmployeeDialog(false);
      setEditingEmployee(null);
      setEmployeeForm({ name: '', employee_id: '' });
      fetchEmployees();
    } catch (error) {
      console.error('Error saving employee:', error);
      setEmployeeError(error.message);
    }
  };

  const handleToggleEmployeeActive = async (employee) => {
    try {
      const response = await fetch(`${API_BASE}/api/obd/workers/${employee.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('authToken')}` },
        body: JSON.stringify({
          name: employee.name,
          is_active: !employee.active
        })
      });

      if (response.ok) {
        fetchEmployees();
      }
    } catch (error) {
      console.error('Error toggling employee status:', error);
    }
  };

  // Delivery Schedule Functions (using skip days logic)
  const updateDeliveryDay = (platform, day, isSkipDay) => {
    setDeliverySchedule(prev => {
      const platformSkipDays = prev[platform] || [];
      const updated = isSkipDay 
        ? [...platformSkipDays, day].filter((d, i, arr) => arr.indexOf(d) === i)
        : platformSkipDays.filter(d => d !== day);
      
      return { ...prev, [platform]: updated };
    });
  };

  // Dispatch Time Functions
  const updateDispatchTime = (platform, time) => {
    setDispatchTimes(prev => ({
      ...prev,
      [platform]: time
    }));
  };

  // Target Arrival Time Functions
  const updateTargetArrivalTime = (platform, time) => {
    setTargetArrivalTimes(prev => ({
      ...prev,
      [platform]: time
    }));
  };

  // Convert 24-hour time to 12-hour AM/PM format
  const convertTo12Hour = (time24) => {
    if (!time24) return '17:00'; // Default to 5:00 PM
    
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours);
    const period = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    
    return `${hour12}:${minutes} ${period}`;
  };

  // Convert 12-hour AM/PM format to 24-hour time
  const convertTo24Hour = (time12) => {
    if (!time12) return '17:00'; // Default to 5:00 PM
    
    const timeRegex = /^(1[0-2]|0?[1-9]):([0-5][0-9]) (AM|PM)$/;
    const match = time12.match(timeRegex);
    
    if (!match) return '17:00';
    
    let hour = parseInt(match[1]);
    const minute = match[2];
    const period = match[3];
    
    if (period === 'AM' && hour === 12) hour = 0;
    if (period === 'PM' && hour !== 12) hour += 12;
    
    return `${hour.toString().padStart(2, '0')}:${minute}`;
  };

  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  // Helper function for factor color coding
  const getFactorColor = (factor) => {
    if (factor > 1.10) return '#ffebee'; // Light red tint (busier days)
    if (factor < 0.90) return '#e3f2fd';  // Light blue tint (quieter days)
    return 'inherit'; // Default gray (normal days)
  };

  // Delivery cost configuration functions
  const updateGlobalWageRate = (field, value) => {
    const numericValue = parseFloat(value) || 0;
    setDeliveryCostConfig(prev => ({
      ...prev,
      [field]: numericValue
    }));
  };

  const updatePlatformCostSetting = (platform, field, value) => {
    const numericValue = parseFloat(value) || 0;
    setDeliveryCostConfig(prev => ({
      ...prev,
      platformSettings: {
        ...prev.platformSettings,
        [platform]: {
          ...prev.platformSettings[platform],
          [field]: numericValue
        }
      }
    }));
  };

  // Calculate total delivery cost per platform
  const calculateDeliveryCost = (platform) => {
    const platformSettings = deliveryCostConfig.platformSettings[platform];
    if (!platformSettings) return 0;

    const warehouseCost = deliveryCostConfig.warehouseWageRate * platformSettings.pickingHours;
    const transportationCost = platformSettings.transportationCost;
    const platformCost = deliveryCostConfig.platformWageRate * platformSettings.putawayHours;

    return warehouseCost + transportationCost + platformCost;
  };

  // Backroom space management functions
  const updateBackroomSpace = (platform, storeNumber, sqft) => {
    const key = `${platform}-${storeNumber}`;
    const numericValue = parseFloat(sqft) || 0;
    setBackroomSpace(prev => ({
      ...prev,
      [key]: numericValue > 0 ? numericValue : null
    }));
  };

  const togglePlatformExpansion = (platform) => {
    setExpandedPlatforms(prev => ({
      ...prev,
      [platform]: !prev[platform]
    }));
  };

  const setBulkBackroomSpace = (platform, sqft, stores) => {
    const numericValue = parseFloat(sqft) || 0;
    if (numericValue <= 0) return;

    const updates = {};
    stores.forEach(store => {
      const key = `${platform}-${store}`;
      updates[key] = numericValue;
    });

    setBackroomSpace(prev => ({ ...prev, ...updates }));
  };

  // Mock store data (in production, this would come from sales files)
  const getPlatformStores = (platform) => {
    const mockStores = {
      'BNA': ['0157', '0158', '0159', '0160'],
      'ATL': ['2028', '2029', '2030', '2031', '2032'],
      'SAV': ['0223', '0224', '0227', '0642'],
      'GSO': ['1234', '1235', '1236'],
      'MEM': ['5000', '5001', '5002'],
      'PNS': ['4000', '4001'],
      'RDU': ['6000', '6001', '6002'],
      'CAE': ['7000', '7001'],
      'TYS': ['8000', '8001'],
      'ASH': ['9000']
    };
    return mockStores[platform] || [];
  };

  // Calculate sales-day-equivalent gap for display (updated for day numbers)
  const calculateDisplayGap = (platform, skipDays) => {
    // Day factors by day number (1=Monday, 7=Sunday)
    const dayFactors = DAY_OF_WEEK_FACTORS[platform] || 
      { Mon: 1.00, Tue: 1.00, Wed: 0.95, Thu: 1.05, Fri: 1.10, Sat: 0.95, Sun: 1.00 };
    
    // Convert to day number factors (1=Monday, ..., 7=Sunday)
    const dayNumberFactors = {
      1: dayFactors.Mon, 2: dayFactors.Tue, 3: dayFactors.Wed, 4: dayFactors.Thu,
      5: dayFactors.Fri, 6: dayFactors.Sat, 7: dayFactors.Sun
    };
    
    // Create delivery array: [1,2,3,4,5,6,7] with true/false for delivery
    const allDays = [1, 2, 3, 4, 5, 6, 7];
    const deliveryArray = allDays.map(dayNum => !skipDays.includes(dayNum));
    
    let maxGapEquivalent = 0;
    let maxCalendarGap = 0;
    
    // For each delivery day, calculate gap to next delivery
    for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
      if (!deliveryArray[dayIndex]) continue; // Skip non-delivery days
      
      let gapEquivalent = 0;
      let calendarDays = 0;
      let checkIndex = (dayIndex + 1) % 7;
      
      // Count consecutive non-delivery days and sum their factors
      while (!deliveryArray[checkIndex] && checkIndex !== dayIndex) {
        const dayNumber = allDays[checkIndex]; // Get actual day number (1-7)
        const factor = dayNumberFactors[dayNumber] || 1.0;
        gapEquivalent += factor;
        calendarDays++;
        checkIndex = (checkIndex + 1) % 7;
        
        // Prevent infinite loop
        if (calendarDays > 7) break;
      }
      
      if (calendarDays > maxCalendarGap || gapEquivalent > maxGapEquivalent) {
        maxCalendarGap = Math.max(maxCalendarGap, calendarDays);
        maxGapEquivalent = Math.max(maxGapEquivalent, gapEquivalent);
      }
    }
    
    // Debug logging for specific platforms
    if (platform === 'ATL' || platform === 'ASH') {
      console.log(`📊 Max Gap calculation for ${platform}:`, {
        skipDays,
        deliveryArray,
        maxGapEquivalent: maxGapEquivalent.toFixed(2),
        maxCalendarGap,
        dayFactors: dayNumberFactors
      });
    }
    
    return maxGapEquivalent;
  };

  // KPI Value/Goal Row Component
  const KPIValueGoalRow = ({ label, kpiKey, unit, goalValue, onGoalChange }) => {
    const [editValue, setEditValue] = useState('');
    const isOverridden = manualOverrides[kpiKey];
    const currentValue = kpiValues[kpiKey] || 'N/A';
    const isEditing = editingKPI === kpiKey;

    return (
      <Grid item xs={12}>
        <Paper sx={{ 
          p: 2, 
          bgcolor: isOverridden ? '#fff3e0' : '#f5f5f5',
          border: isOverridden ? '2px solid #ff9800' : '1px solid #e0e0e0'
        }}>
          <Grid container spacing={2} alignItems="center">
            {/* KPI Label */}
            <Grid item xs={12} sm={3}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {label}
              </Typography>
              {isOverridden && (
                <Chip 
                  label="Manual Override" 
                  size="small" 
                  color="warning" 
                  sx={{ mt: 0.5, fontSize: '0.7rem', height: 20 }}
                />
              )}
            </Grid>

            {/* Current Value */}
            <Grid item xs={12} sm={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 80 }}>
                  Current Value:
                </Typography>
                {isEditing ? (
                  <TextField
                    size="small"
                    type="number"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    sx={{ width: 100 }}
                    autoFocus
                    InputProps={{
                      endAdornment: unit && <Typography variant="caption">{unit}</Typography>
                    }}
                  />
                ) : (
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {currentValue}{unit}
                  </Typography>
                )}
              </Box>
              {isOverridden && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Overridden: {new Date(isOverridden.timestamp).toLocaleString()}
                </Typography>
              )}
            </Grid>

            {/* Goal Value */}
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Goal"
                type="number"
                value={goalValue || ''}
                onChange={(e) => onGoalChange(e.target.value)}
                size="small"
                InputProps={{
                  endAdornment: unit && <Typography variant="caption">{unit}</Typography>
                }}
              />
            </Grid>

            {/* Actions */}
            <Grid item xs={12} sm={3}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {isEditing ? (
                  <>
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      onClick={() => saveManualOverride(kpiKey, editValue)}
                      disabled={!editValue}
                    >
                      Save
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setEditingKPI(null)}
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<span>✏️</span>}
                      onClick={() => {
                        setEditValue(currentValue === 'N/A' ? '' : currentValue);
                        handleEditKPI(kpiKey, label);
                      }}
                    >
                      Edit
                    </Button>
                    {isOverridden && (
                      <Button
                        size="small"
                        variant="text"
                        color="error"
                        onClick={() => clearOverride(kpiKey)}
                      >
                        Clear
                      </Button>
                    )}
                  </>
                )}
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
    );
  };

  // Handler for KPI value changes
  const handleKPIValueChange = async (kpiKey, value) => {
    console.log('💾 Saving KPI value:', kpiKey, '=', value);
    
    try {
      // Update local state immediately for UI responsiveness
      const updated = { ...kpiValues, [kpiKey]: value };
      setKpiValues(updated);
      
      // Convert camelCase to snake_case for API
      let snakeCaseKey;
      if (kpiKey === 'complete4hrs') {
        snakeCaseKey = 'complete_4hrs'; // Special case mapping
      } else {
        snakeCaseKey = kpiKey.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
      }
      console.log('API key:', snakeCaseKey);
      
      // Prepare data object with just this one value
      const updateData = {
        [snakeCaseKey]: parseFloat(value) || value
      };
      
      console.log('Sending to API:', updateData);
      
      // Save to database via API
      const result = await api.updateKpiValues(updateData);
      console.log('✅ API response:', result);
      
      // Also save as manual override for tracking
      await api.saveManualOverride(kpiKey, value, 'Manual Entry');
      console.log('✅ Manual override saved');
      
      // Show success message
      alert(`✅ ${kpiKey} updated to ${value}`);
      
    } catch (error) {
      console.error('❌ Error saving KPI value:', error);
      alert(`Error saving ${kpiKey}: ${error.message}`);
      
      // Revert local state on error
      try {
        const data = await api.getKpiValues();
        const formattedKpiValues = {
          vendorFillRate: data.vendor_fill_rate,
          currentWeek: data.current_week,
          warehouseInStock: data.warehouse_in_stock,
          totalSkuCount: data.total_sku_count,
          onTimeDelivery: data.on_time_delivery,
          totalDeliveries: data.total_deliveries,
          lphPicking: data.lph_picking,
          pickAccuracy: data.pick_accuracy,
          outboundServiceLevel: data.outbound_service_level,
          inventoryAccuracy: data.inventory_accuracy,
          vendorsConvertedSales: data.vendors_converted_sales,
          vendorsConvertedCount: data.vendors_converted_count,
          dockToStock: data.dock_to_stock,
          storageUtilization: data.storage_utilization,
          automationUptime: data.automation_uptime,
          fourWallCost: data.four_wall_cost,
          receivingUph: data.receiving_uph,
          complete4hrs: data.complete_4hrs,
          inventoryAccuracyPlatform: data.inventory_accuracy_platform,
          skuInStockPlatform: data.sku_in_stock_platform,
          scheduleScore: data.schedule_score,
          deliveryCost: data.delivery_cost,
          totalCombinedCost: data.total_combined_cost,
          costCompareBase: data.cost_compare_base,
          varianceBudget: data.variance_budget,
          percentTotalSales: data.percent_total_sales
        };
        setKpiValues(formattedKpiValues);
      } catch (revertError) {
        console.error('Error reverting KPI values:', revertError);
      }
    }
  };

  // Editable KPI Row Component
  const EditableKPIRow = ({ 
    label, 
    kpiKey, 
    unit, 
    currentValue, 
    goalValue, 
    onValueChange, 
    onGoalChange 
  }) => {
    const [isEditingValue, setIsEditingValue] = useState(false);
    const [showWarning, setShowWarning] = useState(false);
    const [tempValue, setTempValue] = useState('');

    const handleEditClick = () => {
      setTempValue(currentValue || '');
      setShowWarning(true);
    };

    const handleSaveValue = async () => {
      console.log('EditableKPIRow: Saving value for', label, ':', tempValue);
      try {
        await onValueChange(kpiKey, tempValue);
        console.log('EditableKPIRow: Value saved successfully for', label);
        setIsEditingValue(false);
        setShowWarning(false);
      } catch (error) {
        console.error('EditableKPIRow: Error saving value for', label, ':', error);
        alert(`Error saving ${label}: ${error.message}`);
      }
    };

    return (
      <>
        <Grid item xs={12}>
          <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
            <Grid container spacing={2} alignItems="center">
              
              {/* KPI Name */}
              <Grid item xs={12} sm={3}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {label}
                </Typography>
              </Grid>

              {/* Current Value */}
              <Grid item xs={12} sm={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 70 }}>
                    Current:
                  </Typography>
                  {isEditingValue ? (
                    <TextField
                      size="small"
                      type="number"
                      value={tempValue}
                      onChange={(e) => setTempValue(e.target.value)}
                      sx={{ width: 100 }}
                      InputProps={{
                        endAdornment: <Typography variant="caption">{unit}</Typography>
                      }}
                    />
                  ) : (
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {currentValue || 'N/A'}{unit}
                    </Typography>
                  )}
                </Box>
              </Grid>

              {/* Goal Value */}
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Goal"
                  type="number"
                  value={goalValue || ''}
                  onChange={(e) => onGoalChange(e.target.value)}
                  size="small"
                  InputProps={{
                    endAdornment: <Typography variant="caption">{unit}</Typography>
                  }}
                />
              </Grid>

              {/* Action Buttons */}
              <Grid item xs={12} sm={3}>
                {isEditingValue ? (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button size="small" variant="contained" onClick={handleSaveValue}>
                      Save
                    </Button>
                    <Button 
                      size="small" 
                      variant="outlined" 
                      onClick={() => {
                        setIsEditingValue(false);
                        setShowWarning(false);
                      }}
                    >
                      Cancel
                    </Button>
                  </Box>
                ) : (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<span>✏️</span>}
                    onClick={handleEditClick}
                  >
                    Edit Value
                  </Button>
                )}
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Warning Dialog */}
        <Dialog open={showWarning} onClose={() => setShowWarning(false)}>
          <DialogTitle sx={{ color: '#ed6c02' }}>
            ⚠️ Manual Override Warning
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mb: 2 }}>
              You are about to manually override the KPI value for:
            </Typography>
            <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>
              {label}
            </Typography>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <strong>Important:</strong> Manual overrides should only be used when:
              <ul style={{ marginTop: 8, marginBottom: 0 }}>
                <li>File upload is not working</li>
                <li>Source data is incorrect</li>
                <li>Temporary correction is needed</li>
              </ul>
            </Alert>
            <Typography variant="body2" color="text.secondary">
              This value will be replaced when you upload a new report file.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowWarning(false)}>Cancel</Button>
            <Button 
              onClick={() => {
                setIsEditingValue(true);
                setShowWarning(false);
              }} 
              variant="contained" 
              color="warning"
            >
              Continue to Edit
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="h4" gutterBottom>
            Configuration
          </Typography>
          <Typography variant="subtitle1" color="textSecondary">
            System settings and management
          </Typography>
        </Grid>

        {storesNeedPlacement > 0 && (
          <Grid item xs={12}>
            <Alert severity="warning" onClick={() => setActiveTab(10)} sx={{ cursor: 'pointer' }}>
              {storesNeedPlacement} new store{storesNeedPlacement === 1 ? '' : 's'} need placement — open the Store Priority tab to rank {storesNeedPlacement === 1 ? 'it' : 'them'}.
            </Alert>
          </Grid>
        )}

        <Grid item xs={12}>
          <Paper sx={{ width: '100%' }}>
            <Tabs 
              value={activeTab} 
              onChange={(e, newValue) => setActiveTab(newValue)}
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab label="User Management" />
              <Tab label="Shift Management" />
              <Tab label="Delivery Schedule" />
              <Tab label="KPI Goals" />
              <Tab label="Employee Management" />
              <Tab label="SKU Categories" />
              <Tab label="SKU Inventory" />
              <Tab label="Ship-Tos" />
              <Tab label="Tab Visibility" />
              <Tab label="Labor Standards" />
              <Tab label="Store Priority" />
            </Tabs>

            {/* User Management Tab */}
            {activeTab === 9 && (
              <LaborStandardsTab isAdmin={isAdmin} currentUser={currentUser} />
            )}

            {activeTab === 10 && (
              <StorePriorityTab isAdmin={isAdmin} />
            )}

            {activeTab === 0 && (
              <Box sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                  <Typography variant="h6">Users</Typography>
                  <Button variant="contained" onClick={() => setUserDialog(true)}>
                    Add New User
                  </Button>
                </Box>

                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Username</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Full Name</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(users || []).map((user) => (
                        <TableRow key={user.id}>
                          <TableCell>{user.username}</TableCell>
                          <TableCell>{user.full_name || '—'}</TableCell>
                          <TableCell>
                            <Chip label={user.role} size="small" color="primary" />
                          </TableCell>
                          <TableCell>
                            {user.created_at
                              ? new Date(user.created_at).toLocaleDateString()
                              : '—'}
                          </TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleResetPassword(user.id)}
                              sx={{ mr: 1 }}
                            >
                              Reset Password
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              onClick={() => handleDeleteUser(user.id)}
                              disabled={user.id === currentUser?.id}
                            >
                              Delete
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* Shift Management Tab */}
            {activeTab === 1 && (
              <Box sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                  <Typography variant="h6">Shifts</Typography>
                  <Button variant="contained" onClick={() => setShiftDialog(true)}>
                    Add New Shift
                  </Button>
                </Box>

                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Shift Name</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Start Time</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>End Time</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Associates</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(shifts || []).map((shift) => (
                        <TableRow key={shift.id}>
                          <TableCell>{shift.name}</TableCell>
                          <TableCell>{shift.startTime}</TableCell>
                          <TableCell>{shift.endTime}</TableCell>
                          <TableCell>{shift.associates.length}</TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              onClick={() => handleDeleteShift(shift.id)}
                            >
                              Delete
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      {shifts.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4 }}>
                            <Typography color="textSecondary">
                              No shifts configured. Add your first shift above.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* Delivery Schedule Tab */}
            {activeTab === 2 && (
              <Box sx={{ p: 3 }}>
                {/* Delivery Cost Parameters */}
                <Typography variant="h6" gutterBottom>
                  Delivery Cost Parameters
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                  Configure labor rates and platform-specific costs for delivery schedule optimization
                </Typography>
                
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Warehouse Hourly Rate"
                      type="number"
                      value={deliveryCostConfig.warehouseWageRate}
                      onChange={(e) => updateGlobalWageRate('warehouseWageRate', e.target.value)}
                      InputProps={{
                        startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                      }}
                      helperText="Average hourly wage for warehouse picking and packing labor"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Platform Hourly Rate"
                      type="number"
                      value={deliveryCostConfig.platformWageRate}
                      onChange={(e) => updateGlobalWageRate('platformWageRate', e.target.value)}
                      InputProps={{
                        startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                      }}
                      helperText="Average hourly wage for receiving and putaway labor at stores"
                    />
                  </Grid>
                </Grid>

                <Typography variant="h6" gutterBottom>
                  Delivery Schedule by Platform
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  ✓ = Delivery day | ✗ = Skip day. Click to toggle skip days (affects inventory targets)
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography variant="caption" color="textSecondary" sx={{ mr: 1 }}>
                    Sales Day Equivalence Factors
                  </Typography>
                  <Tooltip 
                    title="Estimated traffic patterns by day of week. Values >1.0 indicate busier days, <1.0 indicate quieter days. Used in Smart Min/Max calculations."
                    arrow
                  >
                    <Typography variant="caption" sx={{ cursor: 'help', color: '#1976d2' }}>
                      ℹ️
                    </Typography>
                  </Tooltip>
                </Box>

                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Platform</TableCell>
                        {days.map(day => (
                          <TableCell key={day} align="center" sx={{ fontWeight: 600 }}>
                            {day.substring(0, 3)}
                          </TableCell>
                        ))}
                        <TableCell sx={{ fontWeight: 600 }}>Max Gap</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Dispatch Time</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Target Arrival</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Picking Hours</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Putaway Hours</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Transport Cost</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Total Cost</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(platforms || []).map(platform => {
                        // Skip days are stored in deliverySchedule, delivery days are the inverse
                        const skipDays = deliverySchedule[platform] || [];
                        const deliveryDays = days.filter(day => !skipDays.includes(day));
                        
                        // Debug logging for UI state
                        if (platform === 'ATL' || platform === 'ASH') {
                          console.log(`🎨 UI Rendering ${platform}:`, {
                            skipDays,
                            deliveryDays,
                            hasData: !!deliverySchedule[platform],
                            fullScheduleState: deliverySchedule
                          });
                        }
                        
                        // Calculate sales-day-equivalent gap (not simple calendar days)
                        const maxGapEquivalent = calculateDisplayGap(platform, skipDays);
                        
                        return (
                          <TableRow key={platform}>
                            <TableCell sx={{ fontWeight: 500 }}>{platform}</TableCell>
                            {days.map((day, dayIndex) => {
                              // Convert day name to day number (Sunday=0, Monday=1, ..., Saturday=6)
                              // But database uses Monday=1, ..., Sunday=7, so adjust:
                              const dayNumber = dayIndex === 0 ? 7 : dayIndex; // Sunday=7, Monday=1, etc.
                              const isSkipDay = skipDays.includes(dayNumber);
                              const shortDay = day.substring(0, 3); // Mon, Tue, etc.
                              
                              // Debug logging for specific platforms and days
                              if ((platform === 'ATL' && day === 'Saturday') || 
                                  (platform === 'ASH' && day === 'Monday')) {
                                console.log(`🎯 UI Day Check ${platform} ${day}:`, {
                                  day,
                                  dayIndex,
                                  dayNumber,
                                  skipDays,
                                  isSkipDay,
                                  willShow: isSkipDay ? '✗' : '✓'
                                });
                              }
                              const platformFactors = DAY_OF_WEEK_FACTORS[platform] || 
                                { Mon: 1.00, Tue: 1.00, Wed: 0.95, Thu: 1.05, Fri: 1.10, Sat: 0.95, Sun: 1.00 };
                              const factor = platformFactors[shortDay] || 1.0;
                              
                              return (
                                <TableCell key={day} align="center">
                                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    <Typography 
                                      variant="caption" 
                                      sx={{ 
                                        fontSize: '0.7rem',
                                        color: 'textSecondary',
                                        backgroundColor: getFactorColor(factor),
                                        px: 0.5,
                                        borderRadius: 0.5,
                                        mb: 0.5
                                      }}
                                    >
                                      {factor.toFixed(2)}
                                    </Typography>
                                    <Box 
                                      onClick={() => updateDeliveryDay(platform, dayNumber, !isSkipDay)}
                                      sx={{ 
                                        cursor: 'pointer',
                                        fontSize: '1.2rem',
                                        color: isSkipDay ? '#f44336' : '#4caf50',
                                        userSelect: 'none',
                                        padding: 0.5,
                                        borderRadius: 1,
                                        '&:hover': { backgroundColor: '#f5f5f5' }
                                      }}
                                    >
                                      {isSkipDay ? '✗' : '✓'}
                                    </Box>
                                  </Box>
                                </TableCell>
                              );
                            })}
                            <TableCell>
                              <Tooltip 
                                title="Sales-day-equivalent accounts for traffic patterns. Higher values indicate more inventory needed for delivery gaps."
                                arrow
                              >
                                <Chip 
                                  label={`${maxGapEquivalent.toFixed(1)} sales-days`}
                                  size="small" 
                                  color={maxGapEquivalent <= 1.5 ? 'success' : maxGapEquivalent <= 2.5 ? 'warning' : 'error'}
                                />
                              </Tooltip>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                <input
                                  type="time"
                                  step="900"
                                  value={convertTo24Hour(dispatchTimes[platform] || '5:00 PM')}
                                  onChange={(e) => {
                                    const time24 = e.target.value;
                                    const time12 = convertTo12Hour(time24);
                                    updateDispatchTime(platform, time12);
                                    console.log(`Updated ${platform} dispatch time to ${time12}`);
                                  }}
                                  style={{
                                    padding: '8px',
                                    border: '1px solid #ccc',
                                    borderRadius: '4px',
                                    fontSize: '14px',
                                    fontFamily: 'inherit'
                                  }}
                                />
                                <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
                                  {dispatchTimes[platform] || '5:00 PM'}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                <input
                                  type="time"
                                  step="900"
                                  value={convertTo24Hour(targetArrivalTimes[platform] || '9:00 AM')}
                                  onChange={(e) => {
                                    const time24 = e.target.value;
                                    const time12 = convertTo12Hour(time24);
                                    updateTargetArrivalTime(platform, time12);
                                    console.log(`Updated ${platform} target arrival time to ${time12}`);
                                  }}
                                  style={{
                                    padding: '8px',
                                    border: '1px solid #ccc',
                                    borderRadius: '4px',
                                    fontSize: '14px',
                                    fontFamily: 'inherit'
                                  }}
                                />
                                <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
                                  {targetArrivalTimes[platform] || '9:00 AM'}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <TextField
                                size="small"
                                type="number"
                                value={deliveryCostConfig.platformSettings[platform]?.pickingHours || 4.0}
                                onChange={(e) => updatePlatformCostSetting(platform, 'pickingHours', e.target.value)}
                                sx={{ width: 80 }}
                                inputProps={{ min: 0, step: 0.5 }}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                size="small"
                                type="number"
                                value={deliveryCostConfig.platformSettings[platform]?.putawayHours || 3.0}
                                onChange={(e) => updatePlatformCostSetting(platform, 'putawayHours', e.target.value)}
                                sx={{ width: 80 }}
                                inputProps={{ min: 0, step: 0.5 }}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                size="small"
                                type="number"
                                value={deliveryCostConfig.platformSettings[platform]?.transportationCost || 500}
                                onChange={(e) => updatePlatformCostSetting(platform, 'transportationCost', e.target.value)}
                                InputProps={{
                                  startAdornment: <Typography sx={{ mr: 0.5, fontSize: '0.8rem' }}>$</Typography>,
                                }}
                                sx={{ width: 100 }}
                                inputProps={{ min: 0 }}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={`$${calculateDeliveryCost(platform).toFixed(0)}`}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Backroom Space Configuration */}
                <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
                  Backroom Space by Store
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                  Configure available backroom square footage for each store
                </Typography>

                {(platforms || []).map(platform => {
                  const stores = getPlatformStores(platform) || [];
                  const isExpanded = expandedPlatforms[platform];
                  const configuredStores = stores.filter(store => {
                    const key = `${platform}-${store}`;
                    return backroomSpace[key] && backroomSpace[key] > 0;
                  }).length;

                  return (
                    <Paper key={platform} sx={{ mb: 2, p: 2 }}>
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          cursor: 'pointer',
                          py: 1
                        }}
                        onClick={() => togglePlatformExpansion(platform)}
                      >
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {isExpanded ? '▼' : '▶'} {platform} ({stores.length} stores)
                        </Typography>
                        <Chip 
                          label={`${configuredStores} of ${stores.length} configured`}
                          size="small"
                          color={configuredStores === stores.length ? 'success' : configuredStores > 0 ? 'warning' : 'default'}
                        />
                      </Box>

                      {isExpanded && (
                        <Box sx={{ mt: 2 }}>
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 600 }}>Store</TableCell>
                                  <TableCell sx={{ fontWeight: 600 }}>Backroom (sq ft)</TableCell>
                                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {(stores || []).map(store => {
                                  const key = `${platform}-${store}`;
                                  const sqft = backroomSpace[key];
                                  const isConfigured = sqft && sqft > 0;

                                  return (
                                    <TableRow key={store}>
                                      <TableCell>
                                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                          {store} - {platform}-{store} STORE
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        <TextField
                                          size="small"
                                          type="number"
                                          value={sqft || ''}
                                          onChange={(e) => updateBackroomSpace(platform, store, e.target.value)}
                                          placeholder="Enter sq ft"
                                          sx={{ width: 120 }}
                                          inputProps={{ min: 0, step: 0.1 }}
                                        />
                                      </TableCell>
                                      <TableCell>
                                        {isConfigured ? (
                                          <Chip label="✅ Configured" size="small" color="success" />
                                        ) : (
                                          <Chip label="⚠️ Not Set" size="small" color="warning" />
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </TableContainer>

                          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                            <TextField
                              size="small"
                              type="number"
                              placeholder="Bulk sq ft"
                              sx={{ width: 120 }}
                              inputProps={{ min: 0, step: 0.1 }}
                              id={`bulk-${platform}`}
                            />
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => {
                                const input = document.getElementById(`bulk-${platform}`);
                                setBulkBackroomSpace(platform, input.value, stores);
                                input.value = '';
                              }}
                            >
                              Set All {platform} Stores
                            </Button>
                          </Box>
                        </Box>
                      )}
                    </Paper>
                  );
                })}
              </Box>
            )}

            {/* System Settings Tab */}
            {activeTab === 3 && (
              <Box sx={{ p: 3 }}>
                {isLoadingGoals ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress />
                    <Typography variant="body2" sx={{ mt: 2 }}>
                      Loading KPI goals and configuration...
                    </Typography>
                  </Box>
                ) : (
                <>
                  <Typography variant="h5" gutterBottom>
                    KPI Goals & Current Values
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Set target values and view/edit current KPI values. Manual edits should only be used in emergencies.
                  </Typography>

                  <Alert severity="info" sx={{ mb: 3 }}>
                    💡 <strong>Tip:</strong> Use the "Edit" button to manually override values only when file uploads aren't working or data is incorrect.
                  </Alert>

                {/* WAREHOUSES KPIs */}
                <Paper sx={{ p: 3, mb: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ color: '#2e7d32', fontWeight: 600 }}>
                    🏭 Warehouses KPIs
                  </Typography>
                  
                  {/* Inbound (IB) */}
                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 2, mb: 2, fontWeight: 500 }}>
                    Inbound (IB)
                  </Typography>
                  <Grid container spacing={2}>
                    <EditableKPIRow
                      label="Vistar Stated Fill Rate"
                      kpiKey="vendorFillRate"
                      unit="%"
                      currentValue={kpiValues.vendorFillRate}
                      goalValue={kpiGoals.vendorFillRate}
                      onValueChange={handleKPIValueChange}
                      onGoalChange={(val) => handleKPIGoalChange('vendorFillRate', val)}
                    />
                    <EditableKPIRow
                      label="Vendors Converted (Sales)"
                      kpiKey="vendorsConvertedSales"
                      unit="%"
                      currentValue={kpiValues.vendorsConvertedSales}
                      goalValue={kpiGoals.vendorsConvertedSales}
                      onValueChange={handleKPIValueChange}
                      onGoalChange={(val) => handleKPIGoalChange('vendorsConvertedSales', val)}
                    />
                    <EditableKPIRow
                      label="Vendors Converted (Count)"
                      kpiKey="vendorsConvertedCount"
                      unit=""
                      currentValue={kpiValues.vendorsConvertedCount}
                      goalValue={kpiGoals.vendorsConvertedCount}
                      onValueChange={handleKPIValueChange}
                      onGoalChange={(val) => handleKPIGoalChange('vendorsConvertedCount', val)}
                    />
                    <EditableKPIRow
                      label="Dock-to-Stock"
                      kpiKey="dockToStock"
                      unit=" hrs"
                      currentValue={kpiValues.dockToStock}
                      goalValue={kpiGoals.dockToStock}
                      onValueChange={handleKPIValueChange}
                      onGoalChange={(val) => handleKPIGoalChange('dockToStock', val)}
                    />
                  </Grid>

                  {/* Outbound (OB) */}
                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 3, mb: 1, fontWeight: 500 }}>
                    Outbound (OB)
                  </Typography>
                  <Grid container spacing={2}>
                    <EditableKPIRow
                      label="LPH (Picking)"
                      kpiKey="lphPicking"
                      unit=" lines/hr"
                      currentValue={kpiValues.lphPicking}
                      goalValue={kpiGoals.lphPicking}
                      onValueChange={handleKPIValueChange}
                      onGoalChange={(val) => handleKPIGoalChange('lphPicking', val)}
                    />
                    <EditableKPIRow
                      label="Warehouse Fill Rate"
                      kpiKey="pickAccuracy"
                      unit="%"
                      currentValue={kpiValues.pickAccuracy}
                      goalValue={kpiGoals.pickAccuracy}
                      onValueChange={handleKPIValueChange}
                      onGoalChange={(val) => handleKPIGoalChange('pickAccuracy', val)}
                    />
                    <EditableKPIRow
                      label="Outbound Departure"
                      kpiKey="outboundServiceLevel"
                      unit="%"
                      currentValue={kpiValues.outboundServiceLevel}
                      goalValue={kpiGoals.outboundServiceLevel}
                      onValueChange={handleKPIValueChange}
                      onGoalChange={(val) => handleKPIGoalChange('outboundServiceLevel', val)}
                    />
                  </Grid>

                  {/* Inventory Management (IM) */}
                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 3, mb: 1, fontWeight: 500 }}>
                    Inventory Management (IM)
                  </Typography>
                  <Grid container spacing={2}>
                    <EditableKPIRow
                      label="Inventory Accuracy"
                      kpiKey="inventoryAccuracy"
                      unit="%"
                      currentValue={kpiValues.inventoryAccuracy}
                      goalValue={kpiGoals.inventoryAccuracy}
                      onValueChange={handleKPIValueChange}
                      onGoalChange={(val) => handleKPIGoalChange('inventoryAccuracy', val)}
                    />
                    <EditableKPIRow
                      label="SKU In-Stock (Warehouse)"
                      kpiKey="skuInStock"
                      unit="%"
                      currentValue={kpiValues.warehouseInStock}
                      goalValue={kpiGoals.skuInStock}
                      onValueChange={handleKPIValueChange}
                      onGoalChange={(val) => handleKPIGoalChange('skuInStock', val)}
                    />
                  </Grid>

                  {/* Building (Bldg) */}
                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 3, mb: 1, fontWeight: 500 }}>
                    Building (Bldg)
                  </Typography>
                  <Grid container spacing={2}>
                    <EditableKPIRow
                      label="Storage Utilization"
                      kpiKey="storageUtilization"
                      unit="%"
                      currentValue={kpiValues.storageUtilization}
                      goalValue={kpiGoals.storageUtilization}
                      onValueChange={handleKPIValueChange}
                      onGoalChange={(val) => handleKPIGoalChange('storageUtilization', val)}
                    />
                    <EditableKPIRow
                      label="Automation Up-time"
                      kpiKey="automationUptime"
                      unit="%"
                      currentValue={kpiValues.automationUptime}
                      goalValue={kpiGoals.automationUptime}
                      onValueChange={handleKPIValueChange}
                      onGoalChange={(val) => handleKPIGoalChange('automationUptime', val)}
                    />
                    <EditableKPIRow
                      label="4 Wall Cost"
                      kpiKey="fourWallCost"
                      unit="$"
                      currentValue={kpiValues.fourWallCost}
                      goalValue={kpiGoals.fourWallCost}
                      onValueChange={handleKPIValueChange}
                      onGoalChange={(val) => handleKPIGoalChange('fourWallCost', val)}
                    />
                  </Grid>
                </Paper>

                {/* PLATFORMS KPIs */}
                <Paper sx={{ p: 3, mb: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ color: '#ed6c02', fontWeight: 600 }}>
                    🏪 Platforms KPIs
                  </Typography>
                  
                  {/* Inbound (IB) */}
                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 2, mb: 1, fontWeight: 500 }}>
                    Inbound (IB)
                  </Typography>
                  <Grid container spacing={2}>
                    <EditableKPIRow
                      label="On Time Delivery"
                      kpiKey="onTimeDelivery"
                      unit="%"
                      currentValue={kpiValues.onTimeDelivery}
                      goalValue={kpiGoals.onTimeDelivery}
                      onValueChange={handleKPIValueChange}
                      onGoalChange={(val) => handleKPIGoalChange('onTimeDelivery', val)}
                    />
                    <EditableKPIRow
                      label="Receiving UPH in Store"
                      kpiKey="receivingUph"
                      unit=" units/hr"
                      currentValue={kpiValues.receivingUph}
                      goalValue={kpiGoals.receivingUph}
                      onValueChange={handleKPIValueChange}
                      onGoalChange={(val) => handleKPIGoalChange('receivingUph', val)}
                    />
                    
                    {/* Note about N/A KPIs */}
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', pl: 2 }}>
                        📝 Note: Receiving UPH in Store is currently set to display N/A in the dashboard
                      </Typography>
                    </Grid>
                    <EditableKPIRow
                      label="Complete in 4hrs post Truck"
                      kpiKey="complete4hrs"
                      unit="%"
                      currentValue={kpiValues.complete4hrs}
                      goalValue={kpiGoals.complete4hrs}
                      onValueChange={handleKPIValueChange}
                      onGoalChange={(val) => handleKPIGoalChange('complete4hrs', val)}
                    />
                  </Grid>

                  {/* Inventory Control (IC) */}
                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 3, mb: 1, fontWeight: 500 }}>
                    Inventory Control (IC)
                  </Typography>
                  <Grid container spacing={2}>
                    <EditableKPIRow
                      label="Inventory Accuracy (Platform)"
                      kpiKey="inventoryAccuracyPlatform"
                      unit="%"
                      currentValue={kpiValues.inventoryAccuracyPlatform}
                      goalValue={kpiGoals.inventoryAccuracyPlatform}
                      onValueChange={handleKPIValueChange}
                      onGoalChange={(val) => handleKPIGoalChange('inventoryAccuracyPlatform', val)}
                    />
                    <EditableKPIRow
                      label="SKU In-stock (Platform)"
                      kpiKey="skuInStockPlatform"
                      unit="%"
                      currentValue={kpiValues.skuInStockPlatform}
                      goalValue={kpiGoals.skuInStockPlatform}
                      onValueChange={handleKPIValueChange}
                      onGoalChange={(val) => handleKPIGoalChange('skuInStockPlatform', val)}
                    />
                  </Grid>

                  {/* Labor */}
                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 3, mb: 1, fontWeight: 500 }}>
                    Labor
                  </Typography>
                  <Grid container spacing={2}>
                    <EditableKPIRow
                      label="BY Schedule Score (Replenishment)"
                      kpiKey="scheduleScore"
                      unit="%"
                      currentValue={kpiValues.scheduleScore}
                      goalValue={kpiGoals.scheduleScore}
                      onValueChange={handleKPIValueChange}
                      onGoalChange={(val) => handleKPIGoalChange('scheduleScore', val)}
                    />
                  </Grid>

                  {/* Cost */}
                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 3, mb: 1, fontWeight: 500 }}>
                    Cost
                  </Typography>
                  <Grid container spacing={2}>
                    <EditableKPIRow
                      label="Delivery Cost per Delivery"
                      kpiKey="deliveryCost"
                      unit="$"
                      currentValue={kpiValues.deliveryCost}
                      goalValue={kpiGoals.deliveryCost}
                      onValueChange={handleKPIValueChange}
                      onGoalChange={(val) => handleKPIGoalChange('deliveryCost', val)}
                    />
                  </Grid>
                </Paper>

                {/* TOTAL FINANCIAL PERFORMANCE */}
                <Paper sx={{ p: 3, mb: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', fontWeight: 600 }}>
                    💰 Total Financial Performance
                  </Typography>
                  
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <EditableKPIRow
                      label="Total Combined Cost"
                      kpiKey="totalCombinedCost"
                      unit="$"
                      currentValue={kpiValues.totalCombinedCost}
                      goalValue={kpiGoals.totalCombinedCost}
                      onValueChange={handleKPIValueChange}
                      onGoalChange={(val) => handleKPIGoalChange('totalCombinedCost', val)}
                    />
                    <EditableKPIRow
                      label="Cost Compare to Base"
                      kpiKey="costCompareBase"
                      unit="$"
                      currentValue={kpiValues.costCompareBase}
                      goalValue={kpiGoals.costCompareBase}
                      onValueChange={handleKPIValueChange}
                      onGoalChange={(val) => handleKPIGoalChange('costCompareBase', val)}
                    />
                    <EditableKPIRow
                      label="Variance to Budget"
                      kpiKey="varianceBudget"
                      unit="$"
                      currentValue={kpiValues.varianceBudget}
                      goalValue={kpiGoals.varianceBudget}
                      onValueChange={handleKPIValueChange}
                      onGoalChange={(val) => handleKPIGoalChange('varianceBudget', val)}
                    />
                    <EditableKPIRow
                      label="% of Total Sales"
                      kpiKey="percentTotalSales"
                      unit="%"
                      currentValue={kpiValues.percentTotalSales}
                      goalValue={kpiGoals.percentTotalSales}
                      onValueChange={handleKPIValueChange}
                      onGoalChange={(val) => handleKPIGoalChange('percentTotalSales', val)}
                    />
                  </Grid>
                </Paper>

                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                    💾 Changes are saved automatically
                  </Typography>
                </>
                )}
              </Box>
            )}

            {/* Employee Management Tab */}
            {activeTab === 4 && (
              <Box sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                  <Typography variant="h6">Associates (OBD Workers)</Typography>
                  <Button variant="contained" onClick={() => handleOpenEmployeeDialog()}>
                    Add New Associate
                  </Button>
                </Box>

                {loadingEmployees ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress />
                    <Typography variant="body2" sx={{ mt: 2 }}>
                      Loading employees...
                    </Typography>
                  </Box>
                ) : (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Employee ID</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {employees.map((employee) => (
                          <TableRow key={employee.id} sx={{ opacity: employee.active ? 1 : 0.6 }}>
                            <TableCell>{employee.name}</TableCell>
                            <TableCell>
                              {employee.employee_id || (
                                <Typography variant="body2" color="error" sx={{ fontStyle: 'italic' }}>
                                  Not set
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={employee.active ? 'Active' : 'Inactive'}
                                size="small"
                                color={employee.active ? 'success' : 'default'}
                              />
                            </TableCell>
                            <TableCell>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleOpenEmployeeDialog(employee)}
                                sx={{ mr: 1 }}
                              >
                                Edit
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                color={employee.active ? 'warning' : 'success'}
                                onClick={() => handleToggleEmployeeActive(employee)}
                              >
                                {employee.active ? 'Deactivate' : 'Activate'}
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                        {employees.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}>
                              <Typography color="text.secondary">No associates found</Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )}

            {/* SKU Categories Tab */}
            {activeTab === 5 && (
              <Box sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>SKU Category Management</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Assign PULSE categories to SKUs currently classified as Unknown.
                  Overrides apply immediately to future imports.
                </Typography>

                <Tabs
                  value={skuSubTab}
                  onChange={(_, v) => setSkuSubTab(v)}
                  sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
                >
                  <Tab
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        Unknown SKUs
                        {unknownSkus.length > 0 && (
                          <Chip label={unknownSkus.length} size="small" color="warning"
                            sx={{ height: 18, fontSize: '0.7rem' }} />
                        )}
                      </Box>
                    }
                  />
                  <Tab label="Existing Overrides" />
                </Tabs>

                {/* Unknown SKUs sub-tab */}
                {skuSubTab === 0 && (
                  <Box>
                    {skuError && (
                      <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSkuError(null)}>
                        {skuError}
                      </Alert>
                    )}
                    {loadingUnknownSkus ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                        <CircularProgress />
                      </Box>
                    ) : unknownSkus.length === 0 ? (
                      <Box sx={{ textAlign: 'center', mt: 4 }}>
                        <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
                        <Typography variant="h6" color="success.main">No Unknown SKUs</Typography>
                        <Typography variant="body2" color="text.secondary">
                          All SKUs in recent OBD imports have been categorized.
                        </Typography>
                      </Box>
                    ) : (
                      <>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {unknownSkus.length} SKU{unknownSkus.length !== 1 ? 's' : ''} need
                          categorization. Sorted by occurrence count (most frequent first).
                        </Typography>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Material #</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>SAP Material Group</TableCell>
                                <TableCell sx={{ fontWeight: 600, textAlign: 'center', whiteSpace: 'nowrap' }}>
                                  Occurrences
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Assign Category</TableCell>
                                <TableCell />
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {unknownSkus.map((row) => (
                                <TableRow key={row.material}
                                  sx={{
                                    backgroundColor: skuAssignSuccess === row.material
                                      ? 'success.light' : 'inherit',
                                    transition: 'background-color 0.4s',
                                  }}
                                >
                                  <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                                    {row.material}
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" sx={{ maxWidth: 300 }}>
                                      {row.description || '—'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 200 }}>
                                      {row.material_group_desc || '—'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell sx={{ textAlign: 'center' }}>
                                    <Chip
                                      label={row.occurrence_count}
                                      size="small"
                                      color={row.occurrence_count >= 10 ? 'error'
                                        : row.occurrence_count >= 5 ? 'warning' : 'default'}
                                    />
                                  </TableCell>
                                  <TableCell sx={{ minWidth: 180 }}>
                                    <FormControl size="small" fullWidth>
                                      <Select
                                        value={skuSelectedCategory[row.material] ?? ''}
                                        onChange={(e) =>
                                          setSkuSelectedCategory((prev) => ({
                                            ...prev,
                                            [row.material]: e.target.value,
                                          }))
                                        }
                                        disabled={assigningSkuMaterial === row.material}
                                        displayEmpty
                                        renderValue={(val) => {
                                          if (!val) return (
                                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                              Select category...
                                            </Typography>
                                          );
                                          return (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                              {val}
                                              {val === row.recommended_category && (
                                                <Chip label="rec" size="small" color="info"
                                                  sx={{ height: 16, fontSize: '0.6rem', ml: 0.5 }} />
                                              )}
                                            </Box>
                                          );
                                        }}
                                      >
                                        <MenuItem value="" disabled>
                                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                            Select category...
                                          </Typography>
                                        </MenuItem>
                                        {SKU_PULSE_CATEGORIES.map((cat) => (
                                          <MenuItem key={cat} value={cat}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                              {cat}
                                              {cat === row.recommended_category && (
                                                <Chip label="recommended" size="small" color="info"
                                                  sx={{ height: 18, fontSize: '0.65rem' }} />
                                              )}
                                            </Box>
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  </TableCell>
                                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                    <Button
                                      variant="contained"
                                      size="small"
                                      onClick={() => handleSkuAssign(row.material)}
                                      disabled={assigningSkuMaterial === row.material || !skuSelectedCategory[row.material]}
                                      startIcon={assigningSkuMaterial === row.material
                                        ? <CircularProgress size={14} color="inherit" /> : null}
                                    >
                                      Assign
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </>
                    )}
                  </Box>
                )}

                {/* Existing Overrides sub-tab */}
                {skuSubTab === 1 && (
                  <Box>
                    {skuOverridesError && (
                      <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSkuOverridesError(null)}>
                        {skuOverridesError}
                      </Alert>
                    )}
                    {loadingSkuOverrides ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                        <CircularProgress />
                      </Box>
                    ) : skuOverrides.length === 0 ? (
                      <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                        No overrides configured yet.
                      </Typography>
                    ) : (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Material #</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                              <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>SAP Material Group</TableCell>
                              <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>PULSE Category</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Reason</TableCell>
                              <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Set By</TableCell>
                              <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Date</TableCell>
                              <TableCell />
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {skuOverrides.map((row) => (
                              <TableRow key={row.material}>
                                <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                                  {row.material}
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={{ maxWidth: 220 }}>
                                    {row.description || '—'}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 180 }}>
                                    {row.material_group_desc || '—'}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip label={row.pulse_category} size="small" color="primary" variant="outlined" />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" color="text.secondary">
                                    {row.reason || '—'}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">{row.updated_by || '—'}</Typography>
                                </TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                  <Typography variant="body2" color="text.secondary">
                                    {row.created_at
                                      ? new Date(row.created_at).toLocaleDateString('en-US',
                                          { month: 'short', day: 'numeric', year: 'numeric' })
                                      : '—'}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Tooltip title="Remove override">
                                    <IconButton size="small" color="error"
                                      onClick={() => setSkuDeleteTarget(row.material)}>
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Box>
                )}
              </Box>
            )}

            {/* ── SKU Inventory Tab (tab 6) ───────────────────────────────── */}
            {activeTab === 6 && (
              <Box sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>SKU Inventory</Typography>

                <Alert severity="info" sx={{ mb: 2 }}>
                  <strong>Active items only.</strong> This view shows SKUs that have appeared in
                  recent SAP imports. It is not a full historical inventory and does not include
                  items that have stopped shipping. On-hand values reflect the most recent
                  quantity seen for each SKU.
                </Alert>

                {skuInvError && (
                  <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSkuInvError(null)}>
                    {skuInvError}
                  </Alert>
                )}

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                  <TextField
                    size="small"
                    placeholder="Search by material # or description…"
                    value={skuInvSearch}
                    onChange={(e) => setSkuInvSearch(e.target.value)}
                    sx={{ width: 320 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <FormControl size="small" sx={{ minWidth: 220 }}>
                    <Select
                      value={skuInvSort}
                      onChange={(e) => setSkuInvSort(e.target.value)}
                    >
                      <MenuItem value="last_seen_date_desc">Last Seen (newest first)</MenuItem>
                      <MenuItem value="on_hand_desc">On Hand (highest first)</MenuItem>
                      <MenuItem value="material_asc">Material # (A→Z)</MenuItem>
                    </Select>
                  </FormControl>
                  <Box sx={{ flexGrow: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    {skuInvTotal.toLocaleString()} SKU{skuInvTotal !== 1 ? 's' : ''}
                  </Typography>
                </Box>

                {skuInvLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : skuInv.length === 0 ? (
                  <Box sx={{ textAlign: 'center', mt: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      {skuInvSearch
                        ? 'No SKUs match the current search.'
                        : 'No SKUs in inventory yet — populated on the next SAP import.'}
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Material #</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                            <TableCell sx={{ fontWeight: 600, textAlign: 'right', whiteSpace: 'nowrap' }}>On Hand</TableCell>
                            <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>PULSE Category</TableCell>
                            <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Last Seen</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Last Import File</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {skuInv.map((row) => (
                            <TableRow key={row.material} hover>
                              <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                                {row.material}
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ maxWidth: 360 }}>
                                  {row.description || '—'}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                {Number(row.on_hand).toLocaleString()}
                              </TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                {row.pulse_category ? (
                                  <Chip label={row.pulse_category} size="small" />
                                ) : (
                                  <Typography variant="body2" color="text.secondary">—</Typography>
                                )}
                              </TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                {row.last_seen_date
                                  ? new Date(row.last_seen_date).toLocaleDateString('en-US', {
                                      timeZone: 'UTC',
                                      month: '2-digit',
                                      day: '2-digit',
                                      year: 'numeric',
                                    })
                                  : '—'}
                              </TableCell>
                              <TableCell>
                                <Tooltip title={row.last_import_file || ''}>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    noWrap
                                    sx={{ maxWidth: 240 }}
                                  >
                                    {row.last_import_file || '—'}
                                  </Typography>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        {(() => {
                          const start = skuInvPage * SKU_INV_PAGE_SIZE + 1;
                          const end = Math.min(skuInvTotal, (skuInvPage + 1) * SKU_INV_PAGE_SIZE);
                          return `${start.toLocaleString()}–${end.toLocaleString()} of ${skuInvTotal.toLocaleString()}`;
                        })()}
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={skuInvPage === 0}
                        onClick={() => setSkuInvPage((p) => Math.max(0, p - 1))}
                      >
                        Previous
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={(skuInvPage + 1) * SKU_INV_PAGE_SIZE >= skuInvTotal}
                        onClick={() => setSkuInvPage((p) => p + 1)}
                      >
                        Next
                      </Button>
                    </Box>
                  </>
                )}
              </Box>
            )}

            {/* ── Ship-Tos Tab (tab 7) ────────────────────────────────────── */}
            {activeTab === 7 && (
              <Box sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>Ship-To Management</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Set human-readable names for Ship-To codes. Names update immediately across all
                  OBD views. Ship-To numbers are SAP-controlled and cannot be edited here.
                </Typography>

                {shipToError && (
                  <Alert severity="error" sx={{ mb: 2 }} onClose={() => setShipToError(null)}>
                    {shipToError}
                  </Alert>
                )}

                {loadingShipTos ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    {/* ── Section 1: Needs Name ───────────────────────────── */}
                    {(() => {
                      const needsName = shipTos.filter((r) => r.needs_name);
                      return needsName.length > 0 ? (
                        <Box sx={{ mb: 4 }}>
                          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                            Needs Name ({needsName.length})
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            These Ship-To codes appear on OBDs but have no human-readable name.
                          </Typography>
                          <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 600, width: 140 }}>Ship-To #</TableCell>
                                  <TableCell sx={{ fontWeight: 600 }}>Platform</TableCell>
                                  <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                                  <TableCell sx={{ width: 90 }} />
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {needsName.map((row) => (
                                  <TableRow key={row.ship_to_number} sx={{ backgroundColor: '#fff8e1' }}>
                                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                                      {row.ship_to_number}
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2" color="text.secondary">
                                        {row.platform || '—'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <TextField
                                        size="small"
                                        fullWidth
                                        placeholder="e.g. Terminal B Newsstand"
                                        value={shipToEdits[row.ship_to_number] ?? ''}
                                        onChange={(e) =>
                                          setShipToEdits((prev) => ({
                                            ...prev,
                                            [row.ship_to_number]: e.target.value,
                                          }))
                                        }
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') handleShipToSave(row.ship_to_number);
                                        }}
                                        disabled={savingShipTo === row.ship_to_number}
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <Button
                                        variant="contained"
                                        size="small"
                                        onClick={() => handleShipToSave(row.ship_to_number)}
                                        disabled={savingShipTo === row.ship_to_number}
                                        startIcon={savingShipTo === row.ship_to_number
                                          ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                                      >
                                        Save
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>
                      ) : (
                        <Box sx={{ mb: 3, p: 2, backgroundColor: '#e8f5e9', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CheckCircleIcon color="success" fontSize="small" />
                          <Typography variant="body2" color="success.main">
                            All Ship-Tos have names.
                          </Typography>
                        </Box>
                      );
                    })()}

                    <Divider sx={{ mb: 3 }} />

                    {/* ── Section 2: All Ship-Tos ─────────────────────────── */}
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          All Ship-Tos ({shipTos.length})
                        </Typography>
                        <TextField
                          size="small"
                          placeholder="Search by number or name…"
                          value={shipToSearch}
                          onChange={(e) => setShipToSearch(e.target.value)}
                          sx={{ width: 260 }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Box>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, width: 140 }}>Ship-To #</TableCell>
                              <TableCell sx={{ fontWeight: 600, width: 100 }}>Platform</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                              <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', width: 130 }}>Last Updated</TableCell>
                              <TableCell sx={{ fontWeight: 600, width: 120 }}>Updated By</TableCell>
                              <TableCell sx={{ width: 90 }} />
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {shipTos
                              .filter((r) => {
                                if (!shipToSearch) return true;
                                const q = shipToSearch.toLowerCase();
                                return (
                                  r.ship_to_number.toLowerCase().includes(q) ||
                                  (r.name || '').toLowerCase().includes(q)
                                );
                              })
                              .map((row) => (
                                <TableRow
                                  key={row.ship_to_number}
                                  sx={{ backgroundColor: row.needs_name ? '#fff8e1' : 'inherit' }}
                                >
                                  <TableCell sx={{ fontFamily: 'monospace' }}>
                                    {row.ship_to_number}
                                    {!row.in_use && (
                                      <Chip label="unused" size="small" variant="outlined"
                                        sx={{ ml: 1, height: 16, fontSize: '0.6rem' }} />
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" color="text.secondary">
                                      {row.platform || '—'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <TextField
                                      size="small"
                                      fullWidth
                                      placeholder={row.needs_name ? 'No name set — add one' : ''}
                                      value={shipToEdits[row.ship_to_number] ?? row.name ?? ''}
                                      onChange={(e) =>
                                        setShipToEdits((prev) => ({
                                          ...prev,
                                          [row.ship_to_number]: e.target.value,
                                        }))
                                      }
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') handleShipToSave(row.ship_to_number);
                                      }}
                                      disabled={savingShipTo === row.ship_to_number}
                                      sx={{
                                        '& .MuiOutlinedInput-root': {
                                          backgroundColor: row.needs_name ? '#fff8e1' : 'transparent',
                                        },
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                    <Typography variant="body2" color="text.secondary">
                                      {row.updated_at
                                        ? new Date(row.updated_at).toLocaleDateString('en-US', {
                                            month: 'short', day: 'numeric', year: 'numeric',
                                          })
                                        : '—'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" color="text.secondary">
                                      {row.updated_by || '—'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      onClick={() => handleShipToSave(row.ship_to_number)}
                                      disabled={savingShipTo === row.ship_to_number}
                                      startIcon={savingShipTo === row.ship_to_number
                                        ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                                    >
                                      Save
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  </>
                )}
              </Box>
            )}

            {/* ── Tab Visibility Tab (tab 8) ──────────────────────────────── */}
            {activeTab === 8 && (
              <Box sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box>
                    <Typography variant="h6">Tab Visibility</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Control which nav tabs each role can see. Grayed cells are restricted by role permissions and cannot be enabled here.
                    </Typography>
                  </Box>
                  <Tooltip title={!isAdmin ? 'Only admins can save tab visibility settings.' : ''}>
                    <span>
                      <Button
                        variant="contained"
                        startIcon={savingTabVis ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                        onClick={handleTabVisSave}
                        disabled={!isAdmin || savingTabVis}
                        sx={{ ml: 2, flexShrink: 0 }}
                      >
                        Save
                      </Button>
                    </span>
                  </Tooltip>
                </Box>

                {!isAdmin && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Only admins can modify tab visibility. You can view the current settings below.
                  </Alert>
                )}

                {loadingTabVis ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small" sx={{ minWidth: 980 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, minWidth: 100 }}>Role</TableCell>
                          {TAB_DEFS.map(t => (
                            <TableCell key={t.key} align="center" sx={{ fontWeight: 600, fontSize: '0.72rem', whiteSpace: 'nowrap', px: 0.5 }}>
                              {t.label}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {ROLES.map(role => (
                          <TableRow key={role}>
                            <TableCell sx={{ fontWeight: 600, textTransform: 'capitalize' }}>{role}</TableCell>
                            {TAB_DEFS.map(t => {
                              const permAllowed = PERMISSION_FLOOR[role]?.[t.key] ?? false;
                              const isLocked = role === 'admin' && t.key === 'configuration';
                              const draft = tabVisDraft[role]?.[t.key] ?? permAllowed;

                              if (isLocked) {
                                return (
                                  <TableCell key={t.key} align="center" sx={{ px: 0.5 }}>
                                    <Tooltip title="Required — admins must be able to access this page.">
                                      <span>
                                        <Checkbox checked disabled size="small" />
                                      </span>
                                    </Tooltip>
                                  </TableCell>
                                );
                              }

                              if (!permAllowed) {
                                return (
                                  <TableCell key={t.key} align="center" sx={{ backgroundColor: '#f5f5f5', px: 0.5 }}>
                                    <Tooltip title="Restricted by role permissions.">
                                      <span>
                                        <Checkbox checked={false} disabled size="small" />
                                      </span>
                                    </Tooltip>
                                  </TableCell>
                                );
                              }

                              return (
                                <TableCell key={t.key} align="center" sx={{ px: 0.5 }}>
                                  <Checkbox
                                    checked={draft}
                                    onChange={() => handleTabVisToggle(role, t.key, t.label)}
                                    disabled={!isAdmin}
                                    size="small"
                                  />
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Add User Dialog */}
        <Dialog open={userDialog} onClose={() => setUserDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Add New User</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Username"
              value={newUsername}
              onChange={(e) => setNewUsername(e.target.value)}
              margin="normal"
            />

            <TextField
              fullWidth
              label="Full Name (optional)"
              value={newFullName}
              onChange={(e) => setNewFullName(e.target.value)}
              margin="normal"
            />

            <TextField
              fullWidth
              select
              label="Role"
              value={newRole}
              onChange={(e) => setNewRole(e.target.value)}
              margin="normal"
              SelectProps={{ native: true }}
            >
              {USER_ROLES.map((r) => (
                <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>
              ))}
            </TextField>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              A password is generated and shown once after the user is created.
              The Display role is the wall-board kiosk account (30-day sessions, display page only).
              Page access per role is controlled on the Tab Visibility tab.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setUserDialog(false)}>Cancel</Button>
            <Button onClick={handleAddUser} variant="contained">Add User</Button>
          </DialogActions>
        </Dialog>

        {/* Add Shift Dialog */}
        <Dialog open={shiftDialog} onClose={() => setShiftDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Add New Shift</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Shift Name"
              value={newShift.name}
              onChange={(e) => setNewShift(prev => ({ ...prev, name: e.target.value }))}
              margin="normal"
            />

            <TextField
              fullWidth
              type="time"
              label="Start Time"
              value={newShift.startTime}
              onChange={(e) => setNewShift(prev => ({ ...prev, startTime: e.target.value }))}
              margin="normal"
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              fullWidth
              type="time"
              label="End Time"
              value={newShift.endTime}
              onChange={(e) => setNewShift(prev => ({ ...prev, endTime: e.target.value }))}
              margin="normal"
              InputLabelProps={{ shrink: true }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShiftDialog(false)}>Cancel</Button>
            <Button onClick={handleAddShift} variant="contained">Add Shift</Button>
          </DialogActions>
        </Dialog>

        {/* Employee Dialog */}
        <Dialog open={employeeDialog} onClose={() => setEmployeeDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{editingEmployee ? 'Edit Associate' : 'Add New Associate'}</DialogTitle>
          <DialogContent>
            {employeeError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {employeeError}
              </Alert>
            )}
            <TextField
              fullWidth
              label="Name"
              value={employeeForm.name}
              onChange={(e) => setEmployeeForm(prev => ({ ...prev, name: e.target.value }))}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Employee ID"
              value={employeeForm.employee_id}
              onChange={(e) => setEmployeeForm(prev => ({ ...prev, employee_id: e.target.value }))}
              margin="normal"
              required
              helperText="Required for associates to sign on to OBDs"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEmployeeDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveEmployee} variant="contained">
              {editingEmployee ? 'Save Changes' : 'Add Associate'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Override Warning Dialog */}
        <Dialog open={showOverrideWarning} onClose={cancelOverride}>
          <DialogTitle sx={{ color: '#ed6c02' }}>
            ⚠️ Manual Override Warning
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mb: 2 }}>
              You are about to manually override the KPI value for:
            </Typography>
            <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>
              {pendingKPIEdit?.label}
            </Typography>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <strong>Important:</strong> Manual overrides should only be used in emergency situations when:
              <ul style={{ marginTop: 8, marginBottom: 0 }}>
                <li>File upload is not working</li>
                <li>Source data is incorrect</li>
                <li>Temporary correction is needed</li>
              </ul>
            </Alert>
            <Typography variant="body2" color="text.secondary">
              This value will be replaced when you upload a new report file. The manual override will be clearly marked on the dashboard.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={cancelOverride} color="primary">
              Cancel
            </Button>
            <Button onClick={confirmOverride} variant="contained" color="warning">
              Continue to Edit
            </Button>
          </DialogActions>
        </Dialog>

        {/* SKU Override Delete Confirmation Dialog */}
        <Dialog open={Boolean(skuDeleteTarget)} onClose={() => !skuDeleting && setSkuDeleteTarget(null)}>
          <DialogTitle>Remove Override?</DialogTitle>
          <DialogContent>
            <Typography>
              Remove the category override for material <strong>{skuDeleteTarget}</strong>?
              Future imports will revert to automatic classification for this SKU.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSkuDeleteTarget(null)} disabled={skuDeleting}>Cancel</Button>
            <Button color="error" variant="contained" onClick={handleSkuDeleteConfirm} disabled={skuDeleting}
              startIcon={skuDeleting ? <CircularProgress size={14} color="inherit" /> : null}>
              Remove
            </Button>
          </DialogActions>
        </Dialog>

      </Grid>

      {/* Footer */}
      <Box
        sx={{
          mt: 6,
          pt: 2,
          pb: 2,
          borderTop: '1px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 1
        }}
      >
        <Typography variant="caption" color="textSecondary">
          Powered by
        </Typography>
        <img
          src={ClarisLogo}
          alt="Claris AI"
          style={{ height: 40, opacity: 0.7 }}
        />
      </Box>

      {/* Ship-To save feedback */}
      <Snackbar
        open={shipToSnack.open}
        autoHideDuration={3500}
        onClose={() => setShipToSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShipToSnack((s) => ({ ...s, open: false }))}
          severity={shipToSnack.severity}
          sx={{ width: '100%' }}
        >
          {shipToSnack.message}
        </Alert>
      </Snackbar>

      {/* Tab Visibility: hide-tab warning */}
      <Dialog open={tabVisWarnDialog.open} onClose={() => setTabVisWarnDialog(d => ({ ...d, open: false }))} maxWidth="xs" fullWidth>
        <DialogTitle>Hide tab for this role?</DialogTitle>
        <DialogContent>
          <Typography>
            Hiding <strong>{tabVisWarnDialog.label}</strong> for <strong>{tabVisWarnDialog.role}</strong> will remove it from their nav immediately after you save. They can still reach the page directly via URL.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTabVisWarnDialog(d => ({ ...d, open: false }))}>Cancel</Button>
          <Button color="warning" variant="contained" onClick={confirmTabVisHide}>Hide Tab</Button>
        </DialogActions>
      </Dialog>

      {/* Tab Visibility: save feedback */}
      <Snackbar
        open={tabVisSnack.open}
        autoHideDuration={3500}
        onClose={() => setTabVisSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setTabVisSnack(s => ({ ...s, open: false }))}
          severity={tabVisSnack.severity}
          sx={{ width: '100%' }}
        >
          {tabVisSnack.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Configuration;